import * as THREE from 'three';
import {ArcArcIntersectionSolver} from '../../math/ArcArcIntersectionSolver';
import {
    ArcCircleIntersectionSolver,
    type CircularArcIntersectionDefinition2D,
} from '../../math/ArcCircleIntersectionSolver';
import {
    ArcEllipseIntersectionSolver,
    type EllipticalArcIntersectionDefinition2D,
} from '../../math/ArcEllipseIntersectionSolver';
import {ArcLineSegmentIntersectionSolver} from '../../math/ArcLineSegmentIntersectionSolver';
import {CircleCircleIntersectionSolver} from '../../math/CircleCircleIntersectionSolver';
import {CircleLineSegmentIntersectionSolver} from '../../math/CircleLineSegmentIntersectionSolver';
import {EllipseCircleIntersectionSolver} from '../../math/EllipseCircleIntersectionSolver';
import {EllipseEllipseIntersectionSolver} from '../../math/EllipseEllipseIntersectionSolver';
import {EllipseLineSegmentIntersectionSolver} from '../../math/EllipseLineSegmentIntersectionSolver';
import {LineLineIntersectionSolver} from '../../math/LineLineIntersectionSolver';
import {
    isSketchArcObject,
    isSketchCircleObject,
    isSketchEllipseObject,
    isSketchLineObject,
    SketchBezierCurveClosureKind,
    type SketchArcObject,
    type SketchEllipseObject,
    type SketchLineObject,
} from '../../mesh/sketch';
import {
    isSketchParabolaObject,
    isSketchSplineObject,
    isSketchStyleSplineObject,
} from '../../mesh/guards';
import {SKETCH_GEOMETRY_TOLERANCE_CONFIG} from '../config/SketchGeometryToleranceConfig';
import {SketchEllipseParametricGeometryService} from '../geometry/SketchEllipseParametricGeometryService';
import type {SketchCircleObject} from '../../mesh/sketch/SketchCircleObject';
import type {SketchParabolaObject} from '../../mesh/sketch/SketchParabolaObject';
import type {SketchPointObject} from '../../mesh/sketch/SketchPointObject';
import type {SketchSplineObject} from '../../mesh/sketch/SketchSplineObject';
import type {SketchStyleSplineObject} from '../../mesh/sketch/SketchStyleSplineObject';
import type {ReferencePlane} from '../../reference/ReferencePlane';
import {SketchTrimSplineIntersectionService} from './SketchTrimSplineIntersectionService';
import {
    SketchTrimCurveKind,
    SketchTrimEndpointRole,
    SketchTrimExtendSide,
    type SketchTrimCurveObject,
    type SketchTrimCurveData,
    type SketchTrimCurveSamplePoint,
    type SketchTrimInterval,
    type SketchTrimLineData,
    type SketchTrimSegmentRange,
} from './SketchTrimTypes';

/**
 * 线线交点参数结果。
 */
interface SketchTrimIntersectionParameter {
    /** 目标线参数。 */
    targetT: number;
    /** 来源线参数。 */
    sourceT: number;
    /** 交点坐标。 */
    point: THREE.Vector3;
}

/**
 * 边界参数收集选项。
 */
interface SketchTrimBoundaryCollectOptions {
    /** 是否包含 0 和 1 端点参数。 */
    includeEndpoints: boolean;
    /** 是否允许目标线参数越界（用于延伸）。 */
    includeTargetOutside: boolean;
    /** 是否允许来源线参数越界。 */
    includeSourceOutside: boolean;
}

//------ 样条精确裁剪 开始------
/**
 * 精确参数曲线定义。
 */
interface SketchTrimExactCurveDefinition {
    /** 曲线类型。 */
    kind: SketchTrimCurveKind;
    /** 计算参数点。 */
    evaluate: (parameterT: number) => THREE.Vector3;
    /** 曲线是否闭合。 */
    isClosed: boolean;
    /** 可选 Bezier 控制点；样条族通过它进行 De Casteljau 精确拆分与求交。 */
    bezierControlPoints?: THREE.Vector3[];
}

/**
 * 隐式方程定义。
 */
interface SketchTrimImplicitEquation {
    /** 计算隐式方程值，零值表示点位于曲线上。 */
    evaluate: (point: THREE.Vector3) => number;
}

/**
 * 参数根。
 */
interface SketchTrimRootParameter {
    /** 曲线参数。 */
    parameterT: number;
    /** 对应世界坐标。 */
    point: THREE.Vector3;
}
//------ 样条精确裁剪 结束------

/**
 * 草图剪裁几何服务。
 * 职责：
 * 1. 提供线段参数化与交点求解；
 * 2. 提供剪裁区间解析；
 * 3. 提供延伸目标参数解析。
 */
export class SketchTrimGeometryService {
    /** 拓扑容差。 */
    public static readonly TOPOLOGY_TOLERANCE = 1e-6;
    /** 角度容差。 */
    public static readonly ANGLE_TOLERANCE = SKETCH_GEOMETRY_TOLERANCE_CONFIG.trimIntersectionAngular;
    //------ 强力裁剪亚毫米线段容差 开始------
    /** Trim 线性几何有效长度容差。 */
    public static readonly LINEAR_LENGTH_TOLERANCE = SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength;
    /** Trim 线性几何有效长度平方容差。 */
    public static readonly LINEAR_LENGTH_TOLERANCE_SQUARED = SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLengthSquared;
    /** Trim 端点拓扑合并位置容差。 */
    public static readonly ENDPOINT_MERGE_POSITION_TOLERANCE = SKETCH_GEOMETRY_TOLERANCE_CONFIG.collectionClosurePosition;
    //------ 强力裁剪亚毫米线段容差 结束------
    //------ 样条精确裁剪 开始------
    /** 精确曲线求交递归深度。 */
    private static readonly EXACT_INTERSECTION_MAX_DEPTH = 28;
    /** 精确曲线求交参数容差。 */
    private static readonly EXACT_INTERSECTION_PARAMETER_TOLERANCE
        = SKETCH_GEOMETRY_TOLERANCE_CONFIG.trimIntersectionParameter;
    /** 精确曲线求交点位容差。 */
    private static readonly EXACT_INTERSECTION_POINT_TOLERANCE
        = SKETCH_GEOMETRY_TOLERANCE_CONFIG.trimIntersectionDistance;
    /** 样条隐式求根采样段数。 */
    private static readonly EXACT_INTERSECTION_IMPLICIT_SAMPLE_COUNT = 256;
    /** 样条隐式求根函数值容差。 */
    private static readonly EXACT_INTERSECTION_IMPLICIT_VALUE_TOLERANCE = 1e-5;
    //------ 样条精确裁剪 结束------

    /** 权威椭圆参数几何服务。 */
    private readonly ellipseGeometryService: SketchEllipseParametricGeometryService;
    /** 两圆的真实二维交点求解器。 */
    private readonly circleCircleIntersectionSolver: CircleCircleIntersectionSolver;
    /** 圆与有限线段的真实二维交点求解器。 */
    private readonly circleLineSegmentIntersectionSolver: CircleLineSegmentIntersectionSolver;
    /** 椭圆与圆的真实二维交点求解器。 */
    private readonly ellipseCircleIntersectionSolver: EllipseCircleIntersectionSolver;
    /** 椭圆与有限线段的真实二维交点求解器。 */
    private readonly ellipseLineSegmentIntersectionSolver: EllipseLineSegmentIntersectionSolver;
    /** 圆弧与有限线段的真实二维交点求解器。 */
    private readonly arcLineSegmentIntersectionSolver: ArcLineSegmentIntersectionSolver;
    /** 圆弧与圆的真实二维交点求解器。 */
    private readonly arcCircleIntersectionSolver: ArcCircleIntersectionSolver;
    /** 两条圆弧的真实二维交点求解器。 */
    private readonly arcArcIntersectionSolver: ArcArcIntersectionSolver;
    /** 圆弧与椭圆弧的真实二维交点求解器。 */
    private readonly arcEllipseIntersectionSolver: ArcEllipseIntersectionSolver;
    /** 两条二维直线的真实交点求解器。 */
    private readonly lineLineIntersectionSolver: LineLineIntersectionSolver;
    /** 两个二维椭圆的真实交点求解器。 */
    private readonly ellipseEllipseIntersectionSolver: EllipseEllipseIntersectionSolver;
    /** 直线/完整圆与普通样条、样式样条的 Trim 求交服务。 */
    private readonly splineIntersectionService: SketchTrimSplineIntersectionService;

    /**
     * 创建草图裁剪几何服务。
     * @param ellipseGeometryService 权威椭圆参数几何服务。
     * @param ellipseCircleIntersectionSolver 椭圆与圆的真实二维交点求解器。
     * @param ellipseLineSegmentIntersectionSolver 椭圆与有限线段的真实二维交点求解器。
     * @param circleCircleIntersectionSolver 两圆的真实二维交点求解器。
     * @param circleLineSegmentIntersectionSolver 圆与有限线段的真实二维交点求解器。
     * @param arcLineSegmentIntersectionSolver 圆弧与有限线段的真实二维交点求解器。
     * @param arcCircleIntersectionSolver 圆弧与圆的真实二维交点求解器。
     * @param arcArcIntersectionSolver 两条圆弧的真实二维交点求解器。
     * @param arcEllipseIntersectionSolver 圆弧与椭圆弧的真实二维交点求解器。
     * @param lineLineIntersectionSolver 两条二维直线的真实交点求解器。
     * @param ellipseEllipseIntersectionSolver 两个二维椭圆的真实交点求解器。
     * @param splineIntersectionService 直线/完整圆与样条族的 Trim 求交服务。
     */
    public constructor(
        ellipseGeometryService: SketchEllipseParametricGeometryService = new SketchEllipseParametricGeometryService(),
        ellipseCircleIntersectionSolver: EllipseCircleIntersectionSolver = new EllipseCircleIntersectionSolver(),
        ellipseLineSegmentIntersectionSolver: EllipseLineSegmentIntersectionSolver
            = new EllipseLineSegmentIntersectionSolver(),
        circleCircleIntersectionSolver: CircleCircleIntersectionSolver = new CircleCircleIntersectionSolver(),
        circleLineSegmentIntersectionSolver: CircleLineSegmentIntersectionSolver
            = new CircleLineSegmentIntersectionSolver(),
        arcLineSegmentIntersectionSolver: ArcLineSegmentIntersectionSolver
            = new ArcLineSegmentIntersectionSolver(),
        arcCircleIntersectionSolver: ArcCircleIntersectionSolver = new ArcCircleIntersectionSolver(),
        arcArcIntersectionSolver: ArcArcIntersectionSolver = new ArcArcIntersectionSolver(),
        arcEllipseIntersectionSolver: ArcEllipseIntersectionSolver = new ArcEllipseIntersectionSolver(),
        lineLineIntersectionSolver: LineLineIntersectionSolver = new LineLineIntersectionSolver(),
        ellipseEllipseIntersectionSolver: EllipseEllipseIntersectionSolver
            = new EllipseEllipseIntersectionSolver(),
        splineIntersectionService: SketchTrimSplineIntersectionService = new SketchTrimSplineIntersectionService(),
    ) {
        this.ellipseGeometryService = ellipseGeometryService;
        this.circleCircleIntersectionSolver = circleCircleIntersectionSolver;
        this.circleLineSegmentIntersectionSolver = circleLineSegmentIntersectionSolver;
        this.ellipseCircleIntersectionSolver = ellipseCircleIntersectionSolver;
        this.ellipseLineSegmentIntersectionSolver = ellipseLineSegmentIntersectionSolver;
        this.arcLineSegmentIntersectionSolver = arcLineSegmentIntersectionSolver;
        this.arcCircleIntersectionSolver = arcCircleIntersectionSolver;
        this.arcArcIntersectionSolver = arcArcIntersectionSolver;
        this.arcEllipseIntersectionSolver = arcEllipseIntersectionSolver;
        this.lineLineIntersectionSolver = lineLineIntersectionSolver;
        this.ellipseEllipseIntersectionSolver = ellipseEllipseIntersectionSolver;
        this.splineIntersectionService = splineIntersectionService;
    }

    /**
     * 解析线对象几何数据。
     * @param line 草图线。
     * @param segmentIndex 段索引。
     * @returns 线数据。
     */
    public resolveLineData(line: SketchLineObject, segmentIndex: number | null = null): SketchTrimLineData {
        const start = line.startPoint.getPosition();
        const end = line.endPoint.getPosition();

        // 线长度用于校验真实退化线；该判断必须使用草图统一线性容差，避免亚毫米线段被旧拓扑容差误判。
        const length = start.distanceTo(end);
        if (!Number.isFinite(length) || length <= SketchTrimGeometryService.LINEAR_LENGTH_TOLERANCE) {
            console.error(`[SketchTrimGeometryService] 线段长度无效，无法参与剪裁：${line.uuid}`);
        }

        // 参考面法向直接取自线对象的参考面。
        const normal = line.referencePlane.normal.clone().normalize();
        if (!Number.isFinite(normal.x) || !Number.isFinite(normal.y) || !Number.isFinite(normal.z)) {
            console.error(`[SketchTrimGeometryService] 线段法向无效，无法参与剪裁：${line.uuid}`);
            normal.set(0, 0, 1);
        }

        return {
            line,
            segmentIndex,
            start,
            end,
            normal,
            isConstruction: line.isConstruction,
        };
    }

    /**
     * 解析曲线对象几何数据。
     * @param curve 草图曲线对象。
     * @param segmentIndex 段索引。
     * @returns 曲线数据。
     */
    public resolveCurveData(curve: SketchTrimCurveObject, segmentIndex: number | null = null): SketchTrimCurveData {
        const normal = curve.referencePlane.normal.clone().normalize();
        if (!Number.isFinite(normal.x) || !Number.isFinite(normal.y) || !Number.isFinite(normal.z)) {
            console.error(`[SketchTrimGeometryService] 曲线法向无效，无法参与剪裁：${curve.uuid}`);
            normal.set(0, 0, 1);
        }

        if (isSketchLineObject(curve)) {
            const lineData = this.resolveLineData(curve, segmentIndex);
            return {
                curve,
                curveKind: SketchTrimCurveKind.Line,
                segmentIndex,
                normal,
                isConstruction: lineData.isConstruction,
                isClosed: false,
                samplePoints: [
                    {position: lineData.start, parameterT: 0},
                    {position: lineData.end, parameterT: 1},
                ],
            };
        }

        if (isSketchArcObject(curve)) {
            return {
                curve,
                curveKind: SketchTrimCurveKind.Arc,
                segmentIndex,
                normal,
                isConstruction: curve.isConstruction,
                isClosed: false,
                samplePoints: this.collectSamplePointsFromGeometry(curve, false),
            };
        }

        if (isSketchCircleObject(curve)) {
            return {
                curve,
                curveKind: SketchTrimCurveKind.Circle,
                segmentIndex,
                normal,
                isConstruction: curve.isConstruction,
                isClosed: true,
                samplePoints: this.collectSamplePointsFromGeometry(curve, true),
            };
        }

        if (isSketchEllipseObject(curve)) {
            // 椭圆求交已统一收口到显示 geometry，进入求交前必须确保折线顶点已完成刷新。
            if ('updateEllipse' in curve && typeof curve.updateEllipse === 'function') {
                curve.updateEllipse();
            }
            const isClosed = this.isFullEllipse(curve);
            return {
                curve,
                curveKind: SketchTrimCurveKind.Ellipse,
                segmentIndex,
                normal,
                isConstruction: curve.isConstruction,
                isClosed,
                samplePoints: this.collectSamplePointsFromGeometry(curve, isClosed),
            };
        }

        //------ 样条精确裁剪 开始------
        if (isSketchParabolaObject(curve)) {
            return {
                curve,
                curveKind: SketchTrimCurveKind.Parabola,
                segmentIndex,
                normal,
                isConstruction: curve.isConstruction,
                isClosed: false,
                // 抛物线 Trim 求交必须与用户当前看到的显示折线保持一致，
                // 因此直接读取 BufferGeometry 顶点，禁止回退到数学定义重新采样。
                samplePoints: this.collectSamplePointsFromGeometry(curve, false),
            };
        }

        if (isSketchSplineObject(curve)) {
            const isClosed = this.isClosedCurveObject(curve);
            return {
                curve,
                curveKind: SketchTrimCurveKind.Spline,
                segmentIndex,
                normal,
                isConstruction: curve.isConstruction,
                isClosed,
                samplePoints: this.collectSamplePointsFromGeometry(curve, isClosed),
            };
        }

        if (isSketchStyleSplineObject(curve)) {
            const isClosed = this.isClosedCurveObject(curve);
            return {
                curve,
                curveKind: SketchTrimCurveKind.StyleSpline,
                segmentIndex,
                normal,
                isConstruction: curve.isConstruction,
                isClosed,
                samplePoints: this.collectSamplePointsFromGeometry(curve, isClosed),
            };
        }
        //------ 样条精确裁剪 结束------

        console.error(`[SketchTrimGeometryService] 当前曲线类型暂不支持强力剪裁。`);
        const fallbackCurve = curve as SketchTrimCurveObject & {isConstruction?: boolean};
        return {
            curve,
            curveKind: SketchTrimCurveKind.Line,
            segmentIndex,
            normal,
            isConstruction: fallbackCurve.isConstruction ?? false,
            isClosed: false,
            samplePoints: [],
        };
    }

    /**
     * 判断草图曲线自身是否为闭合实体。
     *
     * 该方法只读取曲线定义，不刷新 BufferGeometry，可安全用于高频悬停选择过滤。
     * 集合轮廓中的单条曲线仍属于开放草图段；只有实体自身没有起终端点时才返回 true。
     *
     * @param curve 待判断草图曲线。
     * @returns 完整圆、完整椭圆或自闭合样条返回 true。
     */
    public isClosedCurveObject(curve: SketchTrimCurveObject): boolean {
        if (isSketchCircleObject(curve)) {
            return true;
        }
        if (isSketchEllipseObject(curve)) {
            return this.isFullEllipse(curve);
        }
        if (isSketchSplineObject(curve) || isSketchStyleSplineObject(curve)) {
            return curve.getCurveMetadata().closureKind === SketchBezierCurveClosureKind.Closed;
        }
        return false;
    }

    /**
     * 将值夹到 [0,1]。
     * @param value 输入值。
     * @returns 夹取后的值。
     */
    public clamp01(value: number): number {
        if (!Number.isFinite(value)) {
            console.error(`[SketchTrimGeometryService] 无法夹取非有限值。`);
            return 0;
        }
        if (value <= 0) {
            return 0;
        }
        if (value >= 1) {
            return 1;
        }
        return value;
    }

    /**
     * 将空间点投影到线段参数。
     * @param start 起点。
     * @param end 终点。
     * @param point 投影点。
     * @returns 参数值。
     */
    public projectPointToSegmentParameter(start: THREE.Vector3, end: THREE.Vector3, point: THREE.Vector3): number {
        const segment = end.clone().sub(start);
        const segmentLengthSq = segment.lengthSq();
        if (segmentLengthSq <= SketchTrimGeometryService.LINEAR_LENGTH_TOLERANCE_SQUARED) {
            console.error(`[SketchTrimGeometryService] 退化线段无法投影参数。`);
            return 0;
        }

        // 点向量在段方向上的投影，得到归一化参数。
        const offset = point.clone().sub(start);
        return offset.dot(segment) / segmentLengthSq;
    }

    /**
     * 将空间点投影到曲线参数。
     * @param curveData 曲线数据。
     * @param point 投影点。
     * @returns 归一化参数。
     */
    public projectPointToCurveParameter(curveData: SketchTrimCurveData, point: THREE.Vector3): number {
        const samplePoints = curveData.samplePoints;
        if (samplePoints.length < 2) {
            console.error(`[SketchTrimGeometryService] 采样点数量不足，无法投影曲线参数。`);
            return 0;
        }

        let bestParameter = 0;
        let bestDistanceSq = Number.POSITIVE_INFINITY;

        for (let index = 0; index < samplePoints.length - 1; index += 1) {
            const startSample = samplePoints[index];
            const endSample = samplePoints[index + 1];
            const rawSegmentT = this.projectPointToSegmentParameter(startSample.position, endSample.position, point);
            const clampedSegmentT = this.clamp01(rawSegmentT);
            const projectedPoint = startSample.position.clone().lerp(endSample.position, clampedSegmentT);
            const distanceSq = projectedPoint.distanceToSquared(point);
            if (distanceSq >= bestDistanceSq) {
                continue;
            }

            bestDistanceSq = distanceSq;
            bestParameter = THREE.MathUtils.lerp(
                startSample.parameterT,
                endSample.parameterT,
                clampedSegmentT,
            );
        }

        if (curveData.isClosed && bestParameter >= 1 - SketchTrimGeometryService.TOPOLOGY_TOLERANCE) {
            return 0;
        }
        return this.clamp01(bestParameter);
    }

    /**
     * 计算曲线与一条世界空间线段的精确交点。
     *
     * 说明：
     * 1. 该方法用于强力剪裁拖拽路径命中，目标是一次性解析整段路径与曲线的真实交点；
     * 2. 返回值同时保留目标曲线参数与拖拽线段参数，便于上层按拖拽方向排序；
     * 3. 不再依赖屏幕路径离散采样，避免高速拖拽或短小目标被采样步长漏掉。
     *
     * @param curveData 目标曲线数据。
     * @param segmentStart 拖拽线段起点。
     * @param segmentEnd 拖拽线段终点。
     * @returns 精确交点列表。
     */
    public collectCurveIntersectionsWithSegment(
        curveData: SketchTrimCurveData,
        segmentStart: THREE.Vector3,
        segmentEnd: THREE.Vector3,
    ): Array<{targetT: number; sourceT: number; point: THREE.Vector3}> {
        if (curveData.curveKind === SketchTrimCurveKind.Line) {
            return this.collectExactLineSegmentIntersections(curveData.curve as SketchLineObject, segmentStart, segmentEnd);
        }

        if (curveData.curveKind === SketchTrimCurveKind.Circle) {
            return this.collectExactCircleSegmentIntersections(curveData.curve as SketchCircleObject, segmentStart, segmentEnd);
        }

        if (curveData.curveKind === SketchTrimCurveKind.Arc) {
            return this.collectExactArcSegmentIntersections(curveData.curve as SketchArcObject, segmentStart, segmentEnd);
        }

        if (curveData.curveKind === SketchTrimCurveKind.Ellipse) {
            return this.collectExactEllipseSegmentIntersections(
                curveData.curve as SketchEllipseObject,
                segmentStart,
                segmentEnd,
            );
        }

        if (
            curveData.curveKind === SketchTrimCurveKind.Spline
            || curveData.curveKind === SketchTrimCurveKind.StyleSpline
        ) {
            const exactIntersections = this.splineIntersectionService.collectSegmentIntersections(
                curveData,
                segmentStart,
                segmentEnd,
            );
            if (exactIntersections !== null) {
                return exactIntersections;
            }

            // 开放插值样条在 Worker 权威定义返回前保留既有显示链路，避免交互期间突然失去命中能力。
            return this.collectCurveSegmentIntersectionsBySamples(curveData, segmentStart, segmentEnd);
        }

        if (curveData.curveKind === SketchTrimCurveKind.Parabola) {
            // 强力裁剪拖拽命中以抛物线当前显示折线为准，逐段计算真实线段交点。
            return this.collectCurveSegmentIntersectionsBySamples(curveData, segmentStart, segmentEnd);
        }

        return [];
    }

    /**
     * 根据曲线参数解析世界坐标。
     * @param curveData 曲线数据。
     * @param parameterT 归一化曲线参数。
     * @returns 世界坐标。
     */
    public resolveCurvePointAtParameter(curveData: SketchTrimCurveData, parameterT: number): THREE.Vector3 {
        const normalizedParameter = curveData.isClosed
            ? this.normalizeClosedParameter(parameterT)
            : this.clamp01(parameterT);

        if (curveData.curveKind === SketchTrimCurveKind.Line) {
            const lineData = this.resolveLineData(curveData.curve as SketchLineObject, curveData.segmentIndex);
            return lineData.start.clone().lerp(lineData.end, normalizedParameter);
        }

        if (curveData.curveKind === SketchTrimCurveKind.Circle) {
            const circle = curveData.curve as SketchCircleObject;
            const center = this.resolveCircleCenterWorld(circle);
            const radius = this.resolveCircleRadius(circle);
            const angle = normalizedParameter * Math.PI * 2;
            return new THREE.Vector3(
                center.x + radius * Math.cos(angle),
                center.y + radius * Math.sin(angle),
                center.z,
            );
        }

        if (curveData.curveKind === SketchTrimCurveKind.Arc) {
            const arc = curveData.curve as SketchArcObject;
            const definition = arc.getArcDefinition();
            const sweep = this.computeAngleSweep(definition.startAngle, definition.endAngle, definition.clockwise);
            const angleOffset = sweep * normalizedParameter;
            const angle = definition.clockwise
                ? this.normalizeAngle(definition.startAngle - angleOffset)
                : this.normalizeAngle(definition.startAngle + angleOffset);
            return new THREE.Vector3(
                definition.centerWorld.x + definition.radius * Math.cos(angle),
                definition.centerWorld.y + definition.radius * Math.sin(angle),
                definition.centerWorld.z,
            );
        }

        if (curveData.curveKind === SketchTrimCurveKind.Ellipse) {
            const ellipse = curveData.curve as SketchEllipseObject;
            const definition = ellipse.getEllipseDefinition();
            return this.ellipseGeometryService.evaluateWorldPointAtParameter(
                ellipse.referencePlane,
                definition,
                normalizedParameter,
            );
        }

        if (this.isSplineFamilyCurveKind(curveData.curveKind)) {
            const exactPoint = this.splineIntersectionService.evaluateWorldPoint(
                curveData,
                normalizedParameter,
            );
            if (exactPoint) {
                return exactPoint;
            }

            // 仅在开放插值样条的 Worker 定义尚未就绪时保留显示折线兼容路径。
            return this.resolveCurvePointAtParameterBySamples(curveData, normalizedParameter);
        }

        //------ 样条精确裁剪 开始------
        const exactDefinition = this.createExactCurveDefinition(curveData);
        if (exactDefinition) {
            return exactDefinition.evaluate(normalizedParameter);
        }
        //------ 样条精确裁剪 结束------

        return this.resolveCurvePointAtParameterBySamples(curveData, normalizedParameter);
    }

    /**
     * 根据交点参数语义解析世界坐标。
     *
     * 说明：
     * 1. 直线/圆与样条族的交点参数来自真实 Bezier/B-Spline 数学定义，必须按同一定义恢复坐标；
     * 2. 尚未接入真实求交的旧曲线组合继续保持采样参数语义，避免扩大本次行为变更范围；
     * 3. 该接口主要服务 Trim 全交点调试回显。
     *
     * @param curveData 曲线数据。
     * @param parameterT 归一化交点参数。
     * @returns 与当前交点计算语义一致的世界坐标。
     */
    public resolveCurvePointAtIntersectionParameter(curveData: SketchTrimCurveData, parameterT: number): THREE.Vector3 {
        const normalizedParameter = curveData.isClosed
            ? this.normalizeClosedParameter(parameterT)
            : this.clamp01(parameterT);
        if (this.isSplineFamilyCurveKind(curveData.curveKind)) {
            return this.resolveCurvePointAtParameter(curveData, normalizedParameter);
        }
        return this.resolveCurvePointAtParameterBySamples(curveData, normalizedParameter);
    }

    /**
     * 基于采样点回退解析曲线参数对应的世界点。
     * @param curveData 曲线数据。
     * @param parameterT 曲线参数。
     * @returns 世界坐标。
     */
    private resolveCurvePointAtParameterBySamples(curveData: SketchTrimCurveData, parameterT: number): THREE.Vector3 {
        const samplePoints = curveData.samplePoints;
        if (samplePoints.length === 0) {
            console.error(`[SketchTrimGeometryService] 采样点数量不足，无法解析曲线坐标。`);
            return new THREE.Vector3();
        }

        for (let index = 0; index < samplePoints.length - 1; index += 1) {
            const startSample = samplePoints[index];
            const endSample = samplePoints[index + 1];
            if (
                parameterT < startSample.parameterT - SketchTrimGeometryService.TOPOLOGY_TOLERANCE
                || parameterT > endSample.parameterT + SketchTrimGeometryService.TOPOLOGY_TOLERANCE
            ) {
                continue;
            }

            const segmentSpan = endSample.parameterT - startSample.parameterT;
            const segmentT = Math.abs(segmentSpan) <= SketchTrimGeometryService.TOPOLOGY_TOLERANCE
                ? 0
                : (parameterT - startSample.parameterT) / segmentSpan;
            return startSample.position.clone().lerp(endSample.position, this.clamp01(segmentT));
        }

        return samplePoints[samplePoints.length - 1].position.clone();
    }

    /**
     * 求解两条线的参数交点。
     * @param targetStart 目标线起点。
     * @param targetEnd 目标线终点。
     * @param sourceStart 来源线起点。
     * @param sourceEnd 来源线终点。
     * @param normal 平面法向。
     * @returns 参数交点结果；平行时返回 null。
     */
    public resolveLineIntersectionParameter(
        targetStart: THREE.Vector3,
        targetEnd: THREE.Vector3,
        sourceStart: THREE.Vector3,
        sourceEnd: THREE.Vector3,
        normal: THREE.Vector3,
    ): SketchTrimIntersectionParameter | null {
        const normalizedNormal = normal.clone().normalize();
        if (
            !Number.isFinite(normalizedNormal.x)
            || !Number.isFinite(normalizedNormal.y)
            || !Number.isFinite(normalizedNormal.z)
            || normalizedNormal.lengthSq() <= Number.EPSILON
        ) {
            console.error('[SketchTrimGeometryService] 草图平面法向无效，无法计算直线交点。', {normal});
            return null;
        }

        // 构造与草图平面正交的二维基，保持长度和夹角，使 math 层只处理纯二维线性方程。
        const referenceAxis = Math.abs(normalizedNormal.x) < 0.9
            ? new THREE.Vector3(1, 0, 0)
            : new THREE.Vector3(0, 1, 0);
        const basisX = referenceAxis.cross(normalizedNormal).normalize();
        const basisY = normalizedNormal.clone().cross(basisX).normalize();
        const projectPoint = (point: THREE.Vector3): THREE.Vector2 => {
            const offset = point.clone().sub(targetStart);
            return new THREE.Vector2(offset.dot(basisX), offset.dot(basisY));
        };
        const result = this.lineLineIntersectionSolver.solveSupportingLines(
            {start: projectPoint(targetStart), end: projectPoint(targetEnd)},
            {start: projectPoint(sourceStart), end: projectPoint(sourceEnd)},
        );
        if (result.relation !== 'point') {
            return null;
        }

        const targetDirection = targetEnd.clone().sub(targetStart);
        return {
            targetT: result.intersection.firstParameter,
            sourceT: result.intersection.secondParameter,
            point: targetStart.clone().addScaledVector(
                targetDirection,
                result.intersection.firstParameter,
            ),
        };
    }

    /**
     * 收集目标线与候选线的交点参数。
     * @param target 目标线数据。
     * @param candidates 候选线数据。
     * @param options 选项。
     * @returns 参数列表（已排序去重）。
     */
    public collectBoundaryParameters(
        target: SketchTrimLineData,
        candidates: SketchTrimLineData[],
        options: Partial<SketchTrimBoundaryCollectOptions> = {},
    ): number[] {
        const mergedOptions: SketchTrimBoundaryCollectOptions = {
            includeEndpoints: options.includeEndpoints ?? true,
            includeTargetOutside: options.includeTargetOutside ?? false,
            includeSourceOutside: options.includeSourceOutside ?? false,
        };

        const params: number[] = [];

        // 根据选项决定是否将线段端点作为边界。
        if (mergedOptions.includeEndpoints) {
            params.push(0, 1);
        }

        candidates.forEach((candidate) => {
            // 自身与自身不参与求交。
            if (candidate.line.uuid === target.line.uuid) {
                return;
            }

            const intersection = this.resolveLineIntersectionParameter(
                target.start,
                target.end,
                candidate.start,
                candidate.end,
                target.normal,
            );
            if (!intersection) {
                return;
            }

            // 来源线参数越界时，按选项决定是否过滤。
            if (!mergedOptions.includeSourceOutside) {
                if (
                    intersection.sourceT < -SketchTrimGeometryService.TOPOLOGY_TOLERANCE
                    || intersection.sourceT > 1 + SketchTrimGeometryService.TOPOLOGY_TOLERANCE
                ) {
                    return;
                }
            }

            // 目标线参数越界时，按选项决定是否过滤。
            if (!mergedOptions.includeTargetOutside) {
                if (
                    intersection.targetT < -SketchTrimGeometryService.TOPOLOGY_TOLERANCE
                    || intersection.targetT > 1 + SketchTrimGeometryService.TOPOLOGY_TOLERANCE
                ) {
                    return;
                }
            }

            params.push(intersection.targetT);
        });

        return this.sortAndDedupeParams(params);
    }

    /**
     * 收集目标曲线与候选曲线的交点参数。
     * @param target 目标曲线数据。
     * @param candidates 候选曲线数据。
     * @param includeEndpoints 是否包含开曲线端点。
     * @returns 参数列表（已排序去重）。
     */
    public collectCurveBoundaryParameters(
        target: SketchTrimCurveData,
        candidates: SketchTrimCurveData[],
        includeEndpoints: boolean,
    ): number[] {
        const params: number[] = [];

        if (includeEndpoints && !target.isClosed) {
            params.push(0, 1);
        }

        candidates.forEach((candidate) => {
            if (candidate.curve.uuid === target.curve.uuid) {
                return;
            }
            const intersections = this.collectCurveIntersectionParameters(target, candidate);
            params.push(...intersections);
        });

        return this.sortAndDedupeCurveParams(params, target.isClosed);
    }

    /**
     * 收集目标曲线与直线边界无限支撑线的交点参数。
     *
     * SolidWorks 的 Inside/Outside 边界不受所选直线可见端点限制，因此只约束交点必须落在
     * 目标开放曲线范围内，不约束交点在边界直线上的参数。非直线目标沿其当前显示曲线段逐段求交，
     * 返回值仍保持目标曲线的归一化参数语义。
     *
     * @param target 待裁剪目标曲线。
     * @param boundaryLineData 直线边界数据。
     * @returns 目标曲线参数列表（已排序去重）。
     */
    public collectCurveBoundaryParametersAgainstLineSupport(
        target: SketchTrimCurveData,
        boundaryLineData: SketchTrimLineData,
    ): number[] {
        const exactSplineParameters = this.splineIntersectionService.collectLineSupportParameters(
            target,
            boundaryLineData,
        );
        if (exactSplineParameters !== null) {
            return exactSplineParameters;
        }

        const params: number[] = [];

        for (let targetIndex = 0; targetIndex < target.samplePoints.length - 1; targetIndex += 1) {
            const targetStart = target.samplePoints[targetIndex]!;
            const targetEnd = target.samplePoints[targetIndex + 1]!;
            const intersection = this.resolveLineIntersectionParameter(
                targetStart.position,
                targetEnd.position,
                boundaryLineData.start,
                boundaryLineData.end,
                target.normal,
            );
            if (!intersection) {
                continue;
            }

            // 目标参数必须位于当前显示段，边界直线参数有意不做范围过滤。
            if (
                intersection.targetT < -SketchTrimGeometryService.TOPOLOGY_TOLERANCE
                || intersection.targetT > 1 + SketchTrimGeometryService.TOPOLOGY_TOLERANCE
            ) {
                continue;
            }
            params.push(THREE.MathUtils.lerp(
                targetStart.parameterT,
                targetEnd.parameterT,
                this.clamp01(intersection.targetT),
            ));
        }

        return this.sortAndDedupeCurveParams(params, target.isClosed);
    }

    /**
     * 解析点击参数所在的可剪裁区间。
     * @param boundaries 边界参数集合。
     * @param pickT 点击参数。
     * @returns 剪裁区间；不可剪裁时返回 null。
     */
    public resolveTrimIntervalByPick(boundaries: number[], pickT: number): SketchTrimInterval | null {
        const normalized = this.sortAndDedupeParams(boundaries);
        if (normalized.length < 2) {
            return null;
        }

        for (let index = 0; index < normalized.length - 1; index += 1) {
            const startT = normalized[index];
            const endT = normalized[index + 1];
            if (
                pickT >= startT - SketchTrimGeometryService.TOPOLOGY_TOLERANCE
                && pickT <= endT + SketchTrimGeometryService.TOPOLOGY_TOLERANCE
            ) {
                const interval = this.normalizeInterval(startT, endT);
                if (!interval) {
                    continue;
                }
                return interval;
            }
        }
        return null;
    }

    //------ 强力剪裁闭合曲线区间解析 开始------
    /**
     * 解析闭合曲线点击参数所在的可剪裁区间。
     *
     * 说明：
     * 1. 圆与完整椭圆没有天然起止端点，最后一个交点到第一个交点之间也是有效区间；
     * 2. 该环绕区间需要表达为 `lastT -> firstT + 1`，后续角度换算才能保留正确弧段；
     * 3. 这里不把端点 0/1 当作额外边界，严格以实体交点作为闭合曲线剪裁边界。
     *
     * @param boundaries 边界参数集合。
     * @param pickT 点击参数。
     * @returns 闭合曲线剪裁区间；不可剪裁时返回 null。
     */
    public resolveClosedTrimIntervalByPick(boundaries: number[], pickT: number): SketchTrimInterval | null {
        const normalized = this.sortAndDedupeCurveParams(boundaries, true);
        if (normalized.length < 2) {
            return null;
        }

        const normalizedPickT = this.normalizeClosedParameter(pickT);
        for (let index = 0; index < normalized.length; index += 1) {
            const startT = normalized[index];
            const isLastBoundary = index === normalized.length - 1;
            const endT = isLastBoundary
                ? normalized[0] + 1
                : normalized[index + 1];
            const comparablePickT = isLastBoundary && normalizedPickT < startT
                ? normalizedPickT + 1
                : normalizedPickT;

            if (
                comparablePickT >= startT - SketchTrimGeometryService.TOPOLOGY_TOLERANCE
                && comparablePickT <= endT + SketchTrimGeometryService.TOPOLOGY_TOLERANCE
            ) {
                return this.normalizeClosedTrimInterval(startT, endT);
            }
        }

        return null;
    }
    //------ 强力剪裁闭合曲线区间解析 结束------

    /**
     * 构建剪裁后的保留线段。
     * @param start 原线起点。
     * @param end 原线终点。
     * @param trimInterval 待剪裁区间。
     * @returns 保留段与被剪段。
     */
    public buildLineSegmentsAfterTrim(
        start: THREE.Vector3,
        end: THREE.Vector3,
        trimInterval: SketchTrimInterval,
    ): {
        keptSegments: SketchTrimSegmentRange[];
        trimmedSegment: SketchTrimSegmentRange | null;
    } {
        const trimmedInterval = this.normalizeInterval(trimInterval.startT, trimInterval.endT);
        if (!trimmedInterval) {
            return {
                keptSegments: [],
                trimmedSegment: null,
            };
        }

        const direction = end.clone().sub(start);
        const toPoint = (t: number): THREE.Vector3 => start.clone().addScaledVector(direction, t);

        const keptSegments: SketchTrimSegmentRange[] = [];
        //------ 强力剪裁退化线段防护 开始------
        // 保留段必须同时满足参数区间和真实世界长度，避免历史恢复后留下无法再次参与剪裁的退化线。
        const trimmedSegment = this.createLineSegmentRangeIfValid(
            toPoint,
            trimmedInterval.startT,
            trimmedInterval.endT,
        );
        const left = this.createLineSegmentRangeIfValid(toPoint, 0, trimmedInterval.startT);
        if (left) {
            keptSegments.push(left);
        }

        const right = this.createLineSegmentRangeIfValid(toPoint, trimmedInterval.endT, 1);
        if (right) {
            keptSegments.push(right);
        }
        //------ 强力剪裁退化线段防护 结束------

        return {
            keptSegments,
            trimmedSegment,
        };
    }

    //------ 强力剪裁退化线段防护 开始------
    /**
     * 创建有效的线段区间。
     *
     * 说明：
     * 1. 参数区间有效只能说明拓扑上存在待生成区段；
     * 2. 当原线极短或剪裁点贴近端点时，参数差可能有效但世界长度已经小于剪裁容差；
     * 3. 这类保留段若生成真实 `SketchLineObject`，下一次 Power Trim 扫描会被 `resolveLineData` 拒绝。
     *
     * @param toPoint 参数到世界点的转换函数。
     * @param startT 区段起始参数。
     * @param endT 区段结束参数。
     * @returns 有效区段；长度不足时返回 null。
     */
    private createLineSegmentRangeIfValid(
        toPoint: (t: number) => THREE.Vector3,
        startT: number,
        endT: number,
    ): SketchTrimSegmentRange | null {
        const interval = this.normalizeInterval(startT, endT);
        if (!interval) {
            return null;
        }

        // 真实点位必须在最终建线前计算，用于过滤物理长度退化的残留段。
        const start = toPoint(interval.startT);
        const end = toPoint(interval.endT);
        if (start.distanceTo(end) <= SketchTrimGeometryService.LINEAR_LENGTH_TOLERANCE) {
            return null;
        }

        return {
            start,
            end,
            startT: interval.startT,
            endT: interval.endT,
        };
    }
    //------ 强力剪裁退化线段防护 结束------

    /**
     * 构建开曲线剪裁后的保留参数区间。
     * @param trimInterval 待剪裁区间。
     * @returns 保留区间与被剪区间。
     */
    public buildOpenCurveRangesAfterTrim(trimInterval: SketchTrimInterval): {
        keptRanges: SketchTrimInterval[];
        trimmedRange: SketchTrimInterval | null;
    } {
        const trimmedInterval = this.normalizeInterval(trimInterval.startT, trimInterval.endT);
        if (!trimmedInterval) {
            return {
                keptRanges: [],
                trimmedRange: null,
            };
        }

        const keptRanges: SketchTrimInterval[] = [];
        const left = this.normalizeInterval(0, trimmedInterval.startT);
        if (left) {
            keptRanges.push(left);
        }

        const right = this.normalizeInterval(trimmedInterval.endT, 1);
        if (right) {
            keptRanges.push(right);
        }

        return {
            keptRanges,
            trimmedRange: trimmedInterval,
        };
    }

    /**
     * 构建闭合曲线剪裁后的保留参数区间。
     * @param trimInterval 待剪裁区间。
     * @returns 保留区间；不存在时返回 null。
     */
    public buildClosedCurveKeptRangeAfterTrim(trimInterval: SketchTrimInterval): SketchTrimInterval | null {
        const trimmedInterval = this.normalizeClosedTrimInterval(trimInterval.startT, trimInterval.endT);
        if (!trimmedInterval) {
            return null;
        }

        if (trimmedInterval.endT - trimmedInterval.startT >= 1 - SketchTrimGeometryService.TOPOLOGY_TOLERANCE) {
            return null;
        }

        return {
            startT: trimmedInterval.endT,
            endT: trimmedInterval.startT + 1,
        };
    }

    /**
     * 从目标曲线当前显示几何中提取指定参数区间的连续点列。
     *
     * 设计说明：
     * 1. 样条求交参数来自 `samplePoints`，裁剪落地必须继续使用同一参数语义，避免交点与新端点错位；
     * 2. 闭合曲线允许区间终点大于 `1`，通过复制一个周期的采样参数支持跨越 `0/1` 接缝；
     * 3. 区间首尾始终按参数插值生成，内部保留源显示采样点，从而避免把曲线降级为端点弦线。
     *
     * @param curveData 目标曲线数据。
     * @param range 待提取参数区间。
     * @returns 按曲线前进方向排序的连续世界坐标点列；区间无效时返回空数组。
     */
    public collectCurvePointsForRange(
        curveData: SketchTrimCurveData,
        range: SketchTrimInterval,
    ): THREE.Vector3[] {
        if (!Number.isFinite(range.startT) || !Number.isFinite(range.endT)) {
            return [];
        }

        const startT = curveData.isClosed ? range.startT : this.clamp01(range.startT);
        const endT = curveData.isClosed ? range.endT : this.clamp01(range.endT);
        if (endT - startT <= SketchTrimGeometryService.TOPOLOGY_TOLERANCE) {
            return [];
        }
        if (
            curveData.isClosed
            && endT - startT > 1 + SketchTrimGeometryService.TOPOLOGY_TOLERANCE
        ) {
            return [];
        }

        const points: THREE.Vector3[] = [];
        /**
         * 追加独立曲线点，过滤采样接缝和插值端点产生的连续重复点。
         * @param point 待追加世界坐标。
         */
        const appendPoint = (point: THREE.Vector3): void => {
            const previousPoint = points[points.length - 1];
            if (
                previousPoint
                && previousPoint.distanceToSquared(point) <= SketchTrimGeometryService.LINEAR_LENGTH_TOLERANCE_SQUARED
            ) {
                return;
            }
            points.push(point.clone());
        };

        appendPoint(this.resolveCurvePointAtParameter(curveData, startT));

        if (curveData.isClosed) {
            const firstCycle = Math.floor(startT);
            const lastCycle = Math.ceil(endT);
            for (let cycle = firstCycle; cycle <= lastCycle; cycle += 1) {
                curveData.samplePoints.forEach((samplePoint) => {
                    const extendedParameterT = samplePoint.parameterT + cycle;
                    if (
                        extendedParameterT <= startT + SketchTrimGeometryService.TOPOLOGY_TOLERANCE
                        || extendedParameterT >= endT - SketchTrimGeometryService.TOPOLOGY_TOLERANCE
                    ) {
                        return;
                    }
                    appendPoint(samplePoint.position);
                });
            }
        } else {
            curveData.samplePoints.forEach((samplePoint) => {
                if (
                    samplePoint.parameterT <= startT + SketchTrimGeometryService.TOPOLOGY_TOLERANCE
                    || samplePoint.parameterT >= endT - SketchTrimGeometryService.TOPOLOGY_TOLERANCE
                ) {
                    return;
                }
                appendPoint(samplePoint.position);
            });
        }

        appendPoint(this.resolveCurvePointAtParameter(curveData, endT));
        return points.length >= 2 ? points : [];
    }

    /**
     * 归一化闭合曲线被剪参数区间。
     * @param startT 起点参数。
     * @param endT 终点参数。
     * @returns 归一化结果。
     */
    public normalizeClosedTrimInterval(startT: number, endT: number): SketchTrimInterval | null {
        if (!Number.isFinite(startT) || !Number.isFinite(endT)) {
            return null;
        }

        const normalizedStart = this.normalizeClosedParameter(startT);
        let normalizedEnd = this.normalizeClosedParameter(endT);
        if (normalizedEnd <= normalizedStart + SketchTrimGeometryService.TOPOLOGY_TOLERANCE) {
            normalizedEnd += 1;
        }

        return this.normalizeInterval(normalizedStart, normalizedEnd);
    }

    /**
     * 根据点击参数解析延伸侧。
     * @param pickT 点击参数。
     * @returns 延伸侧。
     */
    public resolveExtendSideByPick(pickT: number): SketchTrimExtendSide {
        if (pickT <= 0.5) {
            return SketchTrimExtendSide.Start;
        }
        return SketchTrimExtendSide.End;
    }

    /**
     * 在交点参数中解析延伸目标参数。
     * @param intersections 交点参数集合。
     * @param side 延伸侧。
     * @returns 目标参数；不存在时返回 null。
     */
    public resolveExtendTargetParameter(intersections: number[], side: SketchTrimExtendSide): number | null {
        const normalized = this.sortAndDedupeParams(intersections);
        if (normalized.length === 0) {
            return null;
        }

        if (side === SketchTrimExtendSide.Start) {
            const candidates = normalized.filter((t) => t < 0 - SketchTrimGeometryService.TOPOLOGY_TOLERANCE);
            if (candidates.length === 0) {
                return null;
            }
            return candidates[candidates.length - 1];
        }

        const candidates = normalized.filter((t) => t > 1 + SketchTrimGeometryService.TOPOLOGY_TOLERANCE);
        if (candidates.length === 0) {
            return null;
        }
        return candidates[0];
    }

    //------ 样条精确裁剪 开始------
    /**
     * 收集涉及样条族的精确曲线交点参数。
     *
     * @param target 目标曲线。
     * @param candidate 候选曲线。
     * @returns 目标曲线参数；不属于样条精确链路时返回 null。
     */
    private collectExactParametricCurveIntersectionParameters(
        target: SketchTrimCurveData,
        candidate: SketchTrimCurveData,
    ): number[] | null {
        const targetDefinition = this.createExactCurveDefinition(target);
        const candidateDefinition = this.createExactCurveDefinition(candidate);
        const targetIsSplineFamily = this.isSplineFamilyCurveKind(target.curveKind);
        const candidateIsSplineFamily = this.isSplineFamilyCurveKind(candidate.curveKind);

        if (!targetDefinition || !candidateDefinition || (!targetIsSplineFamily && !candidateIsSplineFamily)) {
            return null;
        }

        if (targetDefinition.bezierControlPoints && candidateDefinition.bezierControlPoints) {
            return this.collectBezierBezierIntersectionParameters(targetDefinition, candidateDefinition);
        }

        if (targetDefinition.bezierControlPoints) {
            const candidateImplicit = this.createImplicitEquation(candidate);
            if (!candidateImplicit) {
                return [];
            }
            return this.collectBezierImplicitRootParameters(targetDefinition, candidateImplicit)
                .filter((root) => this.isExactPointOnCurveDomain(candidate, root.point))
                .map((root) => root.parameterT);
        }

        if (candidateDefinition.bezierControlPoints) {
            const targetImplicit = this.createImplicitEquation(target);
            if (!targetImplicit) {
                return [];
            }
            return this.collectBezierImplicitRootParameters(candidateDefinition, targetImplicit)
                .filter((root) => this.isExactPointOnCurveDomain(target, root.point))
                .map((root) => this.projectExactPointToCurveParameter(target, root.point))
                .filter((parameter): parameter is number => parameter !== null);
        }

        return [];
    }

    /**
     * 创建精确参数曲线定义。
     *
     * @param curveData Trim 曲线数据。
     * @returns 精确曲线定义；暂不支持时返回 null。
     */
    private createExactCurveDefinition(curveData: SketchTrimCurveData): SketchTrimExactCurveDefinition | null {
        if (curveData.curveKind === SketchTrimCurveKind.Line) {
            const lineData = this.resolveLineData(curveData.curve as SketchLineObject, curveData.segmentIndex);
            return {
                kind: SketchTrimCurveKind.Line,
                isClosed: false,
                evaluate: (parameterT) => lineData.start.clone().lerp(lineData.end, this.clamp01(parameterT)),
            };
        }

        if (curveData.curveKind === SketchTrimCurveKind.Circle) {
            const circle = curveData.curve as SketchCircleObject;
            const center = this.resolveCircleCenterWorld(circle);
            const radius = this.resolveCircleRadius(circle);
            return {
                kind: SketchTrimCurveKind.Circle,
                isClosed: true,
                evaluate: (parameterT) => {
                    const angle = this.normalizeClosedParameter(parameterT) * Math.PI * 2;
                    return new THREE.Vector3(
                        center.x + radius * Math.cos(angle),
                        center.y + radius * Math.sin(angle),
                        center.z,
                    );
                },
            };
        }

        if (curveData.curveKind === SketchTrimCurveKind.Arc) {
            const arc = curveData.curve as SketchArcObject;
            const definition = arc.getArcDefinition();
            return {
                kind: SketchTrimCurveKind.Arc,
                isClosed: false,
                evaluate: (parameterT) => this.resolveArcWorldPointByParameter(definition, this.clamp01(parameterT)),
            };
        }

        if (curveData.curveKind === SketchTrimCurveKind.Ellipse) {
            const ellipse = curveData.curve as SketchEllipseObject;
            const definition = ellipse.getEllipseDefinition();
            return {
                kind: SketchTrimCurveKind.Ellipse,
                isClosed: this.isFullEllipse(ellipse),
                evaluate: (parameterT) => this.resolveEllipseWorldPointByParameter(
                    ellipse,
                    definition,
                    this.isFullEllipse(ellipse),
                    parameterT,
                ),
            };
        }

        if (curveData.curveKind === SketchTrimCurveKind.Parabola) {
            const parabola = curveData.curve as SketchParabolaObject;
            const definition = parabola.getParabolaDefinition();
            return {
                kind: SketchTrimCurveKind.Parabola,
                isClosed: false,
                evaluate: (parameterT) => this.resolveParabolaWorldPointByParameter(definition, this.clamp01(parameterT)),
            };
        }

        if (curveData.curveKind === SketchTrimCurveKind.Spline || curveData.curveKind === SketchTrimCurveKind.StyleSpline) {
            const controlPoints = this.resolveSplineControlPointPositions(curveData.curve);
            return {
                kind: curveData.curveKind,
                isClosed: curveData.isClosed,
                bezierControlPoints: controlPoints,
                evaluate: (parameterT) => this.evaluateBezierPoint(controlPoints, this.clamp01(parameterT)),
            };
        }

        return null;
    }

    /**
     * 判断是否为样条族曲线。
     *
     * @param curveKind 曲线类型。
     * @returns 是样条族曲线时返回 true。
     */
    private isSplineFamilyCurveKind(curveKind: SketchTrimCurveKind): boolean {
        return curveKind === SketchTrimCurveKind.Spline || curveKind === SketchTrimCurveKind.StyleSpline;
    }

    /**
     * 创建隐式曲线方程。
     *
     * @param curveData Trim 曲线数据。
     * @returns 隐式方程；不支持时返回 null。
     */
    private createImplicitEquation(curveData: SketchTrimCurveData): SketchTrimImplicitEquation | null {
        if (curveData.curveKind === SketchTrimCurveKind.Line) {
            const lineData = this.resolveLineData(curveData.curve as SketchLineObject, curveData.segmentIndex);
            const direction = lineData.end.clone().sub(lineData.start);
            return {
                evaluate: (point) => direction.x * (point.y - lineData.start.y) - direction.y * (point.x - lineData.start.x),
            };
        }

        if (curveData.curveKind === SketchTrimCurveKind.Circle) {
            const circle = curveData.curve as SketchCircleObject;
            const center = this.resolveCircleCenterWorld(circle);
            const radius = this.resolveCircleRadius(circle);
            return {
                evaluate: (point) => point.distanceToSquared(center) - radius * radius,
            };
        }

        if (curveData.curveKind === SketchTrimCurveKind.Arc) {
            const arc = curveData.curve as SketchArcObject;
            const definition = arc.getArcDefinition();
            return {
                evaluate: (point) => point.distanceToSquared(definition.centerWorld) - definition.radius * definition.radius,
            };
        }

        if (curveData.curveKind === SketchTrimCurveKind.Ellipse) {
            const ellipse = curveData.curve as SketchEllipseObject;
            const definition = ellipse.getEllipseDefinition();
            const centerLocal = ellipse.referencePlane.worldToLocal(definition.centerWorld);
            const cosineRotation = Math.cos(definition.rotation);
            const sineRotation = Math.sin(definition.rotation);
            return {
                evaluate: (point) => {
                    // 椭圆旋转角定义在草图参考面内，必须先投影到参考面再评估隐式方程。
                    const pointLocal = ellipse.referencePlane.worldToLocal(point);
                    const translatedX = pointLocal.x - centerLocal.x;
                    const translatedY = pointLocal.y - centerLocal.y;
                    const x = (
                        cosineRotation * translatedX + sineRotation * translatedY
                    ) / definition.radiusX;
                    const y = (
                        -sineRotation * translatedX + cosineRotation * translatedY
                    ) / definition.radiusY;
                    return x * x + y * y - 1;
                },
            };
        }

        if (curveData.curveKind === SketchTrimCurveKind.Parabola) {
            const parabola = curveData.curve as SketchParabolaObject;
            const definition = parabola.getParabolaDefinition();
            const frame = this.resolveParabolaFrame(definition);
            return {
                evaluate: (point) => {
                    const localPoint = point.clone().sub(frame.apexWorld);
                    const side = localPoint.dot(frame.sideDir);
                    const axis = localPoint.dot(frame.axisDir);
                    return axis - (side * side) / (4 * frame.focalLength);
                },
            };
        }

        return null;
    }

    /**
     * 使用参数函数和隐式方程求根。
     *
     * @param definition Bezier 曲线定义。
     * @param implicitEquation 隐式方程。
     * @returns 根参数。
     */
    private collectBezierImplicitRootParameters(
        definition: SketchTrimExactCurveDefinition,
        implicitEquation: SketchTrimImplicitEquation,
    ): SketchTrimRootParameter[] {
        const roots: SketchTrimRootParameter[] = [];

        const samples = this.collectImplicitRootSamples(definition, implicitEquation);
        samples.forEach((sample) => {
            this.pushImplicitRootIfNear(
                roots,
                definition,
                implicitEquation,
                sample.parameterT,
                SketchTrimGeometryService.EXACT_INTERSECTION_IMPLICIT_VALUE_TOLERANCE,
            );
        });

        for (let sampleIndex = 0; sampleIndex < samples.length - 1; sampleIndex += 1) {
            const leftSample = samples[sampleIndex];
            const rightSample = samples[sampleIndex + 1];
            if (!leftSample || !rightSample) {
                continue;
            }
            this.pushImplicitBracketRootIfNeeded(roots, definition, implicitEquation, leftSample, rightSample);
        }

        for (let sampleIndex = 1; sampleIndex < samples.length - 1; sampleIndex += 1) {
            const leftSample = samples[sampleIndex - 1];
            const centerSample = samples[sampleIndex];
            const rightSample = samples[sampleIndex + 1];
            if (!leftSample || !centerSample || !rightSample) {
                continue;
            }
            this.pushImplicitMinimumRootIfNeeded(
                roots,
                definition,
                implicitEquation,
                leftSample,
                centerSample,
                rightSample,
            );
        }

        return this.dedupeRootParameters(roots);
    }

    /**
     * 收集隐式方程在 Bezier 参数域上的固定采样值。
     *
     * @param definition Bezier 曲线定义。
     * @param implicitEquation 隐式方程。
     * @returns 可用于求根的有限采样值。
     */
    private collectImplicitRootSamples(
        definition: SketchTrimExactCurveDefinition,
        implicitEquation: SketchTrimImplicitEquation,
    ): {parameterT: number; value: number}[] {
        const samples: {parameterT: number; value: number}[] = [];
        const sampleCount = SketchTrimGeometryService.EXACT_INTERSECTION_IMPLICIT_SAMPLE_COUNT;
        for (let sampleIndex = 0; sampleIndex <= sampleCount; sampleIndex += 1) {
            const parameterT = sampleIndex / sampleCount;
            const value = implicitEquation.evaluate(definition.evaluate(parameterT));
            if (!Number.isFinite(value)) {
                continue;
            }
            samples.push({parameterT, value});
        }
        return samples;
    }

    /**
     * 对存在异号根的采样区间执行二分收敛。
     *
     * @param roots 根列表。
     * @param definition Bezier 曲线定义。
     * @param implicitEquation 隐式方程。
     * @param leftSample 左采样点。
     * @param rightSample 右采样点。
     */
    private pushImplicitBracketRootIfNeeded(
        roots: SketchTrimRootParameter[],
        definition: SketchTrimExactCurveDefinition,
        implicitEquation: SketchTrimImplicitEquation,
        leftSample: {parameterT: number; value: number},
        rightSample: {parameterT: number; value: number},
    ): void {
        if (!this.canScalarIntervalContainRoot(leftSample.value, rightSample.value)) {
            return;
        }
        if (Math.abs(leftSample.value) <= SketchTrimGeometryService.EXACT_INTERSECTION_IMPLICIT_VALUE_TOLERANCE) {
            this.pushImplicitRootIfNear(
                roots,
                definition,
                implicitEquation,
                leftSample.parameterT,
                SketchTrimGeometryService.EXACT_INTERSECTION_IMPLICIT_VALUE_TOLERANCE,
            );
            return;
        }
        if (Math.abs(rightSample.value) <= SketchTrimGeometryService.EXACT_INTERSECTION_IMPLICIT_VALUE_TOLERANCE) {
            this.pushImplicitRootIfNear(
                roots,
                definition,
                implicitEquation,
                rightSample.parameterT,
                SketchTrimGeometryService.EXACT_INTERSECTION_IMPLICIT_VALUE_TOLERANCE,
            );
            return;
        }

        let leftParameterT = leftSample.parameterT;
        let rightParameterT = rightSample.parameterT;
        let leftValue = leftSample.value;
        let rightValue = rightSample.value;
        const maxIterationCount = SketchTrimGeometryService.EXACT_INTERSECTION_MAX_DEPTH + 8;
        for (let iterationIndex = 0; iterationIndex < maxIterationCount; iterationIndex += 1) {
            const midParameterT = (leftParameterT + rightParameterT) / 2;
            const midValue = implicitEquation.evaluate(definition.evaluate(midParameterT));
            if (!Number.isFinite(midValue)) {
                return;
            }
            if (
                Math.abs(midValue) <= SketchTrimGeometryService.EXACT_INTERSECTION_IMPLICIT_VALUE_TOLERANCE
                || rightParameterT - leftParameterT <= SketchTrimGeometryService.EXACT_INTERSECTION_PARAMETER_TOLERANCE
            ) {
                this.pushImplicitRootIfNear(
                    roots,
                    definition,
                    implicitEquation,
                    midParameterT,
                    SketchTrimGeometryService.EXACT_INTERSECTION_IMPLICIT_VALUE_TOLERANCE,
                );
                return;
            }
            if (this.canScalarIntervalContainRoot(leftValue, midValue)) {
                rightParameterT = midParameterT;
                rightValue = midValue;
                continue;
            }
            leftParameterT = midParameterT;
            leftValue = midValue;
        }

        const rootParameterT = (leftParameterT + rightParameterT) / 2;
        this.pushImplicitRootIfNear(
            roots,
            definition,
            implicitEquation,
            rootParameterT,
            SketchTrimGeometryService.EXACT_INTERSECTION_IMPLICIT_VALUE_TOLERANCE,
        );
        void rightValue;
    }

    /**
     * 对同号但存在局部近零极小值的区间执行收敛。
     *
     * @param roots 根列表。
     * @param definition Bezier 曲线定义。
     * @param implicitEquation 隐式方程。
     * @param leftSample 左采样点。
     * @param centerSample 中间采样点。
     * @param rightSample 右采样点。
     */
    private pushImplicitMinimumRootIfNeeded(
        roots: SketchTrimRootParameter[],
        definition: SketchTrimExactCurveDefinition,
        implicitEquation: SketchTrimImplicitEquation,
        leftSample: {parameterT: number; value: number},
        centerSample: {parameterT: number; value: number},
        rightSample: {parameterT: number; value: number},
    ): void {
        const centerAbsValue = Math.abs(centerSample.value);
        if (centerAbsValue > Math.abs(leftSample.value) || centerAbsValue > Math.abs(rightSample.value)) {
            return;
        }

        const minimumRoot = this.refineImplicitMinimumRoot(definition, implicitEquation, leftSample.parameterT, rightSample.parameterT);
        if (!minimumRoot) {
            return;
        }
        roots.push(minimumRoot);
    }

    /**
     * 在参数区间内寻找隐式函数绝对值最小点。
     *
     * @param definition Bezier 曲线定义。
     * @param implicitEquation 隐式方程。
     * @param startT 起始参数。
     * @param endT 结束参数。
     * @returns 近零极小值根；未达到容差时返回 null。
     */
    private refineImplicitMinimumRoot(
        definition: SketchTrimExactCurveDefinition,
        implicitEquation: SketchTrimImplicitEquation,
        startT: number,
        endT: number,
    ): SketchTrimRootParameter | null {
        let leftParameterT = startT;
        let rightParameterT = endT;
        const maxIterationCount = SketchTrimGeometryService.EXACT_INTERSECTION_MAX_DEPTH + 8;
        for (let iterationIndex = 0; iterationIndex < maxIterationCount; iterationIndex += 1) {
            const leftThirdT = leftParameterT + (rightParameterT - leftParameterT) / 3;
            const rightThirdT = rightParameterT - (rightParameterT - leftParameterT) / 3;
            const leftThirdValue = Math.abs(implicitEquation.evaluate(definition.evaluate(leftThirdT)));
            const rightThirdValue = Math.abs(implicitEquation.evaluate(definition.evaluate(rightThirdT)));
            if (!Number.isFinite(leftThirdValue) || !Number.isFinite(rightThirdValue)) {
                return null;
            }
            if (leftThirdValue <= rightThirdValue) {
                rightParameterT = rightThirdT;
                continue;
            }
            leftParameterT = leftThirdT;
        }

        const parameterT = (leftParameterT + rightParameterT) / 2;
        const point = definition.evaluate(parameterT);
        const value = implicitEquation.evaluate(point);
        if (
            !Number.isFinite(value)
            || Math.abs(value) > SketchTrimGeometryService.EXACT_INTERSECTION_IMPLICIT_VALUE_TOLERANCE
        ) {
            return null;
        }
        return {
            parameterT: this.clamp01(parameterT),
            point,
        };
    }

    /**
     * 判断标量区间是否可能包含根。
     *
     * @param leftValue 左值。
     * @param rightValue 右值。
     * @returns 可能包含根时返回 true。
     */
    private canScalarIntervalContainRoot(leftValue: number, rightValue: number): boolean {
        return Math.abs(leftValue) <= 1e-8
            || Math.abs(rightValue) <= 1e-8
            || leftValue * rightValue < 0;
    }

    /**
     * 近零时追加隐式根。
     *
     * @param roots 根列表。
     * @param definition Bezier 曲线定义。
     * @param implicitEquation 隐式方程。
     * @param parameterT 参数。
     * @param epsilon 容差。
     */
    private pushImplicitRootIfNear(
        roots: SketchTrimRootParameter[],
        definition: SketchTrimExactCurveDefinition,
        implicitEquation: SketchTrimImplicitEquation,
        parameterT: number,
        epsilon = 1e-7,
    ): void {
        const point = definition.evaluate(parameterT);
        if (Math.abs(implicitEquation.evaluate(point)) > epsilon) {
            return;
        }
        roots.push({
            parameterT: this.clamp01(parameterT),
            point,
        });
    }

    /**
     * 收集两条 Bezier 曲线的交点参数。
     *
     * @param target 目标 Bezier 曲线。
     * @param candidate 候选 Bezier 曲线。
     * @returns 目标参数。
     */
    private collectBezierBezierIntersectionParameters(
        target: SketchTrimExactCurveDefinition,
        candidate: SketchTrimExactCurveDefinition,
    ): number[] {
        const roots: SketchTrimRootParameter[] = [];
        const visit = (
            targetStartT: number,
            targetEndT: number,
            candidateStartT: number,
            candidateEndT: number,
            depth: number,
        ): void => {
            const targetBox = this.buildCurveIntervalBoundingBox(target, targetStartT, targetEndT);
            const candidateBox = this.buildCurveIntervalBoundingBox(candidate, candidateStartT, candidateEndT);
            if (!this.intersectsBoundingBoxes(targetBox, candidateBox)) {
                return;
            }

            const targetParameterSpan = targetEndT - targetStartT;
            const candidateParameterSpan = candidateEndT - candidateStartT;
            if (
                depth >= SketchTrimGeometryService.EXACT_INTERSECTION_MAX_DEPTH
                || (targetParameterSpan <= SketchTrimGeometryService.EXACT_INTERSECTION_PARAMETER_TOLERANCE
                    && candidateParameterSpan <= SketchTrimGeometryService.EXACT_INTERSECTION_PARAMETER_TOLERANCE)
            ) {
                const targetMidT = (targetStartT + targetEndT) / 2;
                const candidateMidT = (candidateStartT + candidateEndT) / 2;
                const refinedRoot = this.refineBezierBezierIntersectionRoot(target, candidate, targetMidT, candidateMidT);
                if (refinedRoot) {
                    roots.push(refinedRoot);
                }
                return;
            }

            const targetMidT = (targetStartT + targetEndT) / 2;
            const candidateMidT = (candidateStartT + candidateEndT) / 2;
            if (targetParameterSpan >= candidateParameterSpan) {
                visit(targetStartT, targetMidT, candidateStartT, candidateEndT, depth + 1);
                visit(targetMidT, targetEndT, candidateStartT, candidateEndT, depth + 1);
                return;
            }
            visit(targetStartT, targetEndT, candidateStartT, candidateMidT, depth + 1);
            visit(targetStartT, targetEndT, candidateMidT, candidateEndT, depth + 1);
        };

        visit(0, 1, 0, 1, 0);
        return this.dedupeRootParameters(roots).map((root) => root.parameterT);
    }

    /**
     * 使用牛顿迭代细化两条 Bezier 曲线的交点。
     *
     * @param target 目标 Bezier 曲线。
     * @param candidate 候选 Bezier 曲线。
     * @param initialTargetT 目标曲线初始参数。
     * @param initialCandidateT 候选曲线初始参数。
     * @returns 收敛后的目标曲线根；未收敛时返回 null。
     */
    private refineBezierBezierIntersectionRoot(
        target: SketchTrimExactCurveDefinition,
        candidate: SketchTrimExactCurveDefinition,
        initialTargetT: number,
        initialCandidateT: number,
    ): SketchTrimRootParameter | null {
        let targetParameterT = this.clamp01(initialTargetT);
        let candidateParameterT = this.clamp01(initialCandidateT);
        const derivativeStep = 1e-5;
        const maxIterationCount = SketchTrimGeometryService.EXACT_INTERSECTION_MAX_DEPTH;

        for (let iterationIndex = 0; iterationIndex < maxIterationCount; iterationIndex += 1) {
            const targetPoint = target.evaluate(targetParameterT);
            const candidatePoint = candidate.evaluate(candidateParameterT);
            const residual = targetPoint.clone().sub(candidatePoint);
            if (residual.length() <= SketchTrimGeometryService.EXACT_INTERSECTION_POINT_TOLERANCE) {
                return {
                    parameterT: targetParameterT,
                    point: targetPoint.clone().lerp(candidatePoint, 0.5),
                };
            }

            const targetDerivative = this.evaluateCurveDerivative(target, targetParameterT, derivativeStep);
            const candidateDerivative = this.evaluateCurveDerivative(candidate, candidateParameterT, derivativeStep);
            const determinant = candidateDerivative.x * targetDerivative.y - targetDerivative.x * candidateDerivative.y;
            if (Math.abs(determinant) <= SketchTrimGeometryService.TOPOLOGY_TOLERANCE) {
                return null;
            }

            const targetDelta = (candidateDerivative.y * residual.x - candidateDerivative.x * residual.y) / determinant;
            const candidateDelta = (targetDerivative.y * residual.x - targetDerivative.x * residual.y) / determinant;
            targetParameterT = this.clamp01(targetParameterT + targetDelta);
            candidateParameterT = this.clamp01(candidateParameterT + candidateDelta);
        }

        const targetPoint = target.evaluate(targetParameterT);
        const candidatePoint = candidate.evaluate(candidateParameterT);
        if (targetPoint.distanceTo(candidatePoint) > SketchTrimGeometryService.EXACT_INTERSECTION_POINT_TOLERANCE) {
            return null;
        }
        return {
            parameterT: targetParameterT,
            point: targetPoint.clone().lerp(candidatePoint, 0.5),
        };
    }

    /**
     * 通过中心差分计算参数曲线导数。
     *
     * @param definition 曲线定义。
     * @param parameterT 参数。
     * @param derivativeStep 差分步长。
     * @returns 导数向量。
     */
    private evaluateCurveDerivative(
        definition: SketchTrimExactCurveDefinition,
        parameterT: number,
        derivativeStep: number,
    ): THREE.Vector3 {
        const previousT = this.clamp01(parameterT - derivativeStep);
        const nextT = this.clamp01(parameterT + derivativeStep);
        const span = nextT - previousT;
        if (span <= SketchTrimGeometryService.TOPOLOGY_TOLERANCE) {
            return new THREE.Vector3(0, 0, 0);
        }
        return definition.evaluate(nextT).sub(definition.evaluate(previousT)).divideScalar(span);
    }

    /**
     * 构建曲线参数区间包围盒。
     *
     * @param definition 曲线定义。
     * @param startT 起点参数。
     * @param endT 终点参数。
     * @returns 包围盒。
     */
    private buildCurveIntervalBoundingBox(
        definition: SketchTrimExactCurveDefinition,
        startT: number,
        endT: number,
    ): {minX: number; maxX: number; minY: number; maxY: number} {
        // Bezier 子段的控制多边形包围盒一定包住曲线本体，递归求交不能只看端点。
        if (definition.bezierControlPoints) {
            const points = this.extractBezierControlPointRange(definition.bezierControlPoints, startT, endT);
            return this.buildBoundingBoxByPoints(points);
        }

        const middleT = (startT + endT) / 2;
        const points = [
            definition.evaluate(startT),
            definition.evaluate(middleT),
            definition.evaluate(endT),
        ];
        return this.buildBoundingBoxByPoints(points);
    }

    /**
     * 根据点集构建二维包围盒。
     *
     * @param points 点集。
     * @returns 包围盒。
     */
    private buildBoundingBoxByPoints(points: THREE.Vector3[]): {minX: number; maxX: number; minY: number; maxY: number} {
        const xs = points.map((point) => point.x);
        const ys = points.map((point) => point.y);
        const margin = SketchTrimGeometryService.EXACT_INTERSECTION_POINT_TOLERANCE;
        return {
            minX: Math.min(...xs) - margin,
            maxX: Math.max(...xs) + margin,
            minY: Math.min(...ys) - margin,
            maxY: Math.max(...ys) + margin,
        };
    }

    /**
     * 精确提取 Bezier 子段控制点。
     *
     * @param controlPoints 原始控制点。
     * @param startT 起始参数。
     * @param endT 结束参数。
     * @returns 子段控制点。
     */
    private extractBezierControlPointRange(
        controlPoints: THREE.Vector3[],
        startT: number,
        endT: number,
    ): THREE.Vector3[] {
        const normalizedStartT = this.clamp01(startT);
        const normalizedEndT = this.clamp01(endT);
        if (normalizedEndT <= normalizedStartT) {
            return [this.evaluateBezierPoint(controlPoints, normalizedStartT)];
        }

        const [, rightPart] = this.splitBezierControlPoints(controlPoints, normalizedStartT);
        const localEndT = normalizedStartT >= 1
            ? 1
            : (normalizedEndT - normalizedStartT) / (1 - normalizedStartT);
        const [segmentControlPoints] = this.splitBezierControlPoints(rightPart, localEndT);
        return segmentControlPoints;
    }

    /**
     * 使用 De Casteljau 算法拆分 Bezier 控制点。
     *
     * @param controlPoints 原始控制点。
     * @param parameterT 拆分参数。
     * @returns 左右子段控制点。
     */
    private splitBezierControlPoints(controlPoints: THREE.Vector3[], parameterT: number): [THREE.Vector3[], THREE.Vector3[]] {
        if (controlPoints.length < 2) {
            console.error(`[SketchTrimGeometryService] 拆分 Bezier 曲线失败：控制点数量不足。`);
            const fallbackPoints = controlPoints.map((point) => point.clone());
            return [fallbackPoints, fallbackPoints.map((point) => point.clone())];
        }

        const t = this.clamp01(parameterT);
        const levels: THREE.Vector3[][] = [controlPoints.map((point) => point.clone())];
        while (levels[levels.length - 1].length > 1) {
            const previousLevel = levels[levels.length - 1];
            levels.push(previousLevel.slice(0, -1).map((point, index) => point.clone().lerp(previousLevel[index + 1], t)));
        }

        const leftControlPoints = levels.map((level) => level[0].clone());
        const rightControlPoints = levels.map((level) => level[level.length - 1].clone()).reverse();
        return [leftControlPoints, rightControlPoints];
    }

    /**
     * 判断二维包围盒是否相交。
     *
     * @param left 左包围盒。
     * @param right 右包围盒。
     * @returns 相交时返回 true。
     */
    private intersectsBoundingBoxes(
        left: {minX: number; maxX: number; minY: number; maxY: number},
        right: {minX: number; maxX: number; minY: number; maxY: number},
    ): boolean {
        return left.minX <= right.maxX
            && left.maxX >= right.minX
            && left.minY <= right.maxY
            && left.maxY >= right.minY;
    }

    /**
     * 根参数去重。
     *
     * @param roots 原始根列表。
     * @returns 去重后的根列表。
     */
    private dedupeRootParameters(roots: SketchTrimRootParameter[]): SketchTrimRootParameter[] {
        return roots
            .sort((left, right) => left.parameterT - right.parameterT)
            .reduce<SketchTrimRootParameter[]>((dedupedRoots, root) => {
                const previous = dedupedRoots[dedupedRoots.length - 1];
                if (
                    previous
                    && Math.abs(previous.parameterT - root.parameterT) <= 1e-5
                    && previous.point.distanceTo(root.point) <= 1e-4
                ) {
                    return dedupedRoots;
                }
                dedupedRoots.push(root);
                return dedupedRoots;
            }, []);
    }

    /**
     * 将点投影回精确曲线参数。
     *
     * @param curveData 目标曲线数据。
     * @param point 交点。
     * @returns 目标参数；不在曲线上时返回 null。
     */
    private projectExactPointToCurveParameter(curveData: SketchTrimCurveData, point: THREE.Vector3): number | null {
        if (curveData.curveKind === SketchTrimCurveKind.Line) {
            const lineData = this.resolveLineData(curveData.curve as SketchLineObject, curveData.segmentIndex);
            return this.clamp01(this.projectPointToSegmentParameter(lineData.start, lineData.end, point));
        }
        if (curveData.curveKind === SketchTrimCurveKind.Circle) {
            return this.projectPointToCircleParameter(curveData.curve as SketchCircleObject, point);
        }
        if (curveData.curveKind === SketchTrimCurveKind.Arc) {
            return this.projectPointToArcParameter(curveData.curve as SketchArcObject, point);
        }
        if (curveData.curveKind === SketchTrimCurveKind.Ellipse) {
            return this.projectPointToEllipseParameter(curveData.curve as SketchEllipseObject, point);
        }
        if (curveData.curveKind === SketchTrimCurveKind.Parabola) {
            return this.projectPointToParabolaParameter(curveData.curve as SketchParabolaObject, point);
        }

        return this.projectPointToCurveParameter(curveData, point);
    }

    /**
     * 判断交点是否落在曲线真实可见参数域内。
     *
     * @param curveData 曲线数据。
     * @param point 待检查点。
     * @returns 落在曲线段上时返回 true。
     */
    private isExactPointOnCurveDomain(curveData: SketchTrimCurveData, point: THREE.Vector3): boolean {
        if (curveData.curveKind === SketchTrimCurveKind.Line) {
            const lineData = this.resolveLineData(curveData.curve as SketchLineObject, curveData.segmentIndex);
            const parameterT = this.projectPointToSegmentParameter(lineData.start, lineData.end, point);
            if (
                parameterT < -SketchTrimGeometryService.TOPOLOGY_TOLERANCE
                || parameterT > 1 + SketchTrimGeometryService.TOPOLOGY_TOLERANCE
            ) {
                return false;
            }
            const projectedPoint = lineData.start.clone().lerp(lineData.end, this.clamp01(parameterT));
            return projectedPoint.distanceTo(point) <= SketchTrimGeometryService.EXACT_INTERSECTION_POINT_TOLERANCE;
        }

        return this.projectExactPointToCurveParameter(curveData, point) !== null;
    }

    /**
     * 解析样条控制点世界坐标。
     *
     * @param curve 样条曲线对象。
     * @returns 控制点世界坐标列表。
     */
    private resolveSplineControlPointPositions(curve: SketchTrimCurveObject): THREE.Vector3[] {
        if (isSketchSplineObject(curve) || isSketchStyleSplineObject(curve)) {
            return curve.getControlPoints().map((point: SketchPointObject) => point.getPosition());
        }
        console.error(`[SketchTrimGeometryService] 非样条对象无法解析样条控制点。`);
        return [];
    }

    /**
     * 计算 Bezier 曲线点。
     *
     * @param controlPoints 控制点列表。
     * @param parameterT 参数。
     * @returns 曲线点。
     */
    private evaluateBezierPoint(controlPoints: THREE.Vector3[], parameterT: number): THREE.Vector3 {
        if (controlPoints.length === 0) {
            console.error(`[SketchTrimGeometryService] Bezier 曲线缺少控制点。`);
            return new THREE.Vector3();
        }
        let points = controlPoints.map((point) => point.clone());
        const t = this.clamp01(parameterT);
        while (points.length > 1) {
            points = points.slice(0, -1).map((point, index) => point.lerp(points[index + 1], t));
        }
        return points[0];
    }

    /**
     * 按归一化参数计算圆弧点。
     *
     * @param definition 圆弧定义。
     * @param parameterT 参数。
     * @returns 世界点。
     */
    private resolveArcWorldPointByParameter(
        definition: ReturnType<SketchArcObject['getArcDefinition']>,
        parameterT: number,
    ): THREE.Vector3 {
        const sweep = this.computeAngleSweep(definition.startAngle, definition.endAngle, definition.clockwise);
        const angle = definition.clockwise
            ? this.normalizeAngle(definition.startAngle - sweep * parameterT)
            : this.normalizeAngle(definition.startAngle + sweep * parameterT);
        return new THREE.Vector3(
            definition.centerWorld.x + definition.radius * Math.cos(angle),
            definition.centerWorld.y + definition.radius * Math.sin(angle),
            definition.centerWorld.z,
        );
    }

    /**
     * 按归一化参数计算椭圆点。
     *
     * @param definition 椭圆定义。
     * @param isFull 是否完整椭圆。
     * @param parameterT 参数。
     * @returns 世界点。
     */
    private resolveEllipseWorldPointByParameter(
        ellipse: SketchEllipseObject,
        definition: ReturnType<SketchEllipseObject['getEllipseDefinition']>,
        isFull: boolean,
        parameterT: number,
    ): THREE.Vector3 {
        const t = isFull ? this.normalizeClosedParameter(parameterT) : this.clamp01(parameterT);
        return this.ellipseGeometryService.evaluateWorldPointAtParameter(
            ellipse.referencePlane,
            definition,
            t,
        );
    }

    /**
     * 解析抛物线局部框架。
     *
     * @param definition 抛物线定义。
     * @returns 局部框架。
     */
    private resolveParabolaFrame(definition: ReturnType<SketchParabolaObject['getParabolaDefinition']>): {
        apexWorld: THREE.Vector3;
        axisDir: THREE.Vector3;
        sideDir: THREE.Vector3;
        focalLength: number;
    } {
        const axisDir = definition.focalWorld.clone().sub(definition.apexWorld).normalize();
        const sideDir = new THREE.Vector3(-axisDir.y, axisDir.x, axisDir.z).normalize();
        const focalLength = definition.focalWorld.distanceTo(definition.apexWorld);
        if (!Number.isFinite(focalLength) || focalLength <= SketchTrimGeometryService.TOPOLOGY_TOLERANCE) {
            console.error(`[SketchTrimGeometryService] 抛物线焦距无效，无法参与剪裁。`);
            return {
                apexWorld: definition.apexWorld.clone(),
                axisDir: new THREE.Vector3(1, 0, 0),
                sideDir: new THREE.Vector3(0, 1, 0),
                focalLength: 1,
            };
        }
        return {
            apexWorld: definition.apexWorld.clone(),
            axisDir,
            sideDir,
            focalLength,
        };
    }

    /**
     * 按归一化参数计算抛物线点。
     *
     * @param definition 抛物线定义。
     * @param parameterT 参数。
     * @returns 世界点。
     */
    private resolveParabolaWorldPointByParameter(
        definition: ReturnType<SketchParabolaObject['getParabolaDefinition']>,
        parameterT: number,
    ): THREE.Vector3 {
        const frame = this.resolveParabolaFrame(definition);
        const parabolaParameter = THREE.MathUtils.lerp(
            definition.startParameter,
            definition.endParameter,
            this.clamp01(parameterT),
        );
        const axisOffset = (parabolaParameter * parabolaParameter) / (4 * frame.focalLength);
        return frame.apexWorld.clone()
            .add(frame.sideDir.clone().multiplyScalar(parabolaParameter))
            .add(frame.axisDir.clone().multiplyScalar(axisOffset));
    }

    /**
     * 将世界点投影为抛物线归一化参数。
     *
     * @param parabola 抛物线对象。
     * @param point 世界点。
     * @returns 归一化参数；点不在可见抛物线段时返回 null。
     */
    private projectPointToParabolaParameter(parabola: SketchParabolaObject, point: THREE.Vector3): number | null {
        const definition = parabola.getParabolaDefinition();
        const frame = this.resolveParabolaFrame(definition);
        const localPoint = point.clone().sub(frame.apexWorld);
        const side = localPoint.dot(frame.sideDir);
        const axis = localPoint.dot(frame.axisDir);
        const residual = Math.abs(axis - (side * side) / (4 * frame.focalLength));
        if (residual > 1e-5) {
            return null;
        }
        const span = definition.endParameter - definition.startParameter;
        if (Math.abs(span) <= SketchTrimGeometryService.TOPOLOGY_TOLERANCE) {
            return null;
        }
        const parameterT = (side - definition.startParameter) / span;
        if (parameterT < -SketchTrimGeometryService.TOPOLOGY_TOLERANCE || parameterT > 1 + SketchTrimGeometryService.TOPOLOGY_TOLERANCE) {
            return null;
        }
        return this.clamp01(parameterT);
    }
    //------ 样条精确裁剪 结束------

    /**
     * 判断指定点是否位于两条边界之间。
     * 说明：
     * 1. 当目标线与两条边界都不相交时，SolidWorks 仍允许对位于边界之间的开放线段执行在内剪除；
     * 2. 平行边界按带状区域判定，非平行边界按两线较小夹角形成的扇区判定；
     * 3. 该能力用于 inside / outside 模式的“双不相交”合法分支。
     * @param boundaryAStart 第一边界起点。
     * @param boundaryAEnd 第一边界终点。
     * @param boundaryBStart 第二边界起点。
     * @param boundaryBEnd 第二边界终点。
     * @param point 待判断点。
     * @param normal 草图平面法向。
     * @returns 若点位于边界之间返回 true。
     */
    public isPointBetweenBoundaries(
        boundaryAStart: THREE.Vector3,
        boundaryAEnd: THREE.Vector3,
        boundaryBStart: THREE.Vector3,
        boundaryBEnd: THREE.Vector3,
        point: THREE.Vector3,
        normal: THREE.Vector3,
    ): boolean {
        const boundaryADirection = boundaryAEnd.clone().sub(boundaryAStart);
        const boundaryBDirection = boundaryBEnd.clone().sub(boundaryBStart);

        if (boundaryADirection.lengthSq() <= SketchTrimGeometryService.TOPOLOGY_TOLERANCE ** 2) {
            console.error(`[SketchTrimGeometryService] 第一条边界退化，无法判定内外区域。`);
            return false;
        }
        if (boundaryBDirection.lengthSq() <= SketchTrimGeometryService.TOPOLOGY_TOLERANCE ** 2) {
            console.error(`[SketchTrimGeometryService] 第二条边界退化，无法判定内外区域。`);
            return false;
        }

        const normalizedBoundaryADirection = boundaryADirection.clone().normalize();
        const normalizedBoundaryBDirection = boundaryBDirection.clone().normalize();
        const parallelMeasure = Math.abs(normalizedBoundaryADirection.dot(normalizedBoundaryBDirection));

        if (1 - parallelMeasure <= SketchTrimGeometryService.TOPOLOGY_TOLERANCE) {
            const projectionAxis = normalizedBoundaryADirection.clone().cross(normal).normalize();
            const boundaryAProjection = projectionAxis.dot(boundaryAStart);
            const boundaryBProjection = projectionAxis.dot(boundaryBStart);
            const pointProjection = projectionAxis.dot(point);
            const minProjection = Math.min(boundaryAProjection, boundaryBProjection);
            const maxProjection = Math.max(boundaryAProjection, boundaryBProjection);
            return pointProjection >= minProjection - SketchTrimGeometryService.TOPOLOGY_TOLERANCE
                && pointProjection <= maxProjection + SketchTrimGeometryService.TOPOLOGY_TOLERANCE;
        }

        const boundaryIntersection = this.resolveLineIntersectionParameter(
            boundaryAStart,
            boundaryAEnd,
            boundaryBStart,
            boundaryBEnd,
            normal,
        );
        if (!boundaryIntersection) {
            return false;
        }

        const pointVector = point.clone().sub(boundaryIntersection.point);
        if (pointVector.lengthSq() <= SketchTrimGeometryService.TOPOLOGY_TOLERANCE ** 2) {
            return true;
        }

        const normalizedPointVector = pointVector.normalize();
        const angleBetweenLines = this.resolveUnsignedAngleBetweenDirections(
            normalizedBoundaryADirection,
            normalizedBoundaryBDirection,
        );
        const angleToBoundaryA = this.resolveUnsignedAngleBetweenDirections(
            normalizedPointVector,
            normalizedBoundaryADirection,
        );
        const angleToBoundaryB = this.resolveUnsignedAngleBetweenDirections(
            normalizedPointVector,
            normalizedBoundaryBDirection,
        );

        return angleToBoundaryA + angleToBoundaryB
            <= angleBetweenLines + SketchTrimGeometryService.ANGLE_TOLERANCE;
    }

    //------ Corner 点击侧保留规则 开始------
    /**
     * 在 Corner 模式下根据点击位置解析应移动的端点。
     * 说明：
     * 1. 点击靠近起点侧时，保留起点侧，移动终点到交点；
     * 2. 点击靠近终点侧时，保留终点侧，移动起点到交点；
        * 3. 当理论交点位于线段外侧时，当前场景属于“仅延长到角点”，必须优先按交点外侧方向决定端点；
        * 4. 点击接近中点时，回退到交点参数规则以保持旧行为稳定。
     * @param intersectionT 交点参数。
     * @param pickT 点击参数。
     * @returns 端点角色。
     */
    public resolveEndpointRoleByIntersectionAndPick(
        intersectionT: number,
        pickT: number,
    ): SketchTrimEndpointRole {
        if (!Number.isFinite(intersectionT) || !Number.isFinite(pickT)) {
            console.error(`[SketchTrimGeometryService] Corner 端点解析失败：参数必须为有限值。`);
            return SketchTrimEndpointRole.Start;
        }

        // 说明：交点落在线段外侧时，本质是延长场景，不应再由点击侧改写为“裁剪另一端”。
        if (
            intersectionT < -SketchTrimGeometryService.TOPOLOGY_TOLERANCE
            || intersectionT > 1 + SketchTrimGeometryService.TOPOLOGY_TOLERANCE
        ) {
            return this.resolveEndpointRoleByIntersection(intersectionT);
        }

        const centerDelta = Math.abs(pickT - 0.5);
        if (centerDelta <= SketchTrimGeometryService.TOPOLOGY_TOLERANCE) {
            return this.resolveEndpointRoleByIntersection(intersectionT);
        }

        if (pickT < 0.5) {
            return SketchTrimEndpointRole.End;
        }
        return SketchTrimEndpointRole.Start;
    }
    //------ Corner 点击侧保留规则 结束------

    /**
     * 根据交点参数解析应移动的端点。
     * @param intersectionT 交点参数。
     * @returns 端点角色。
     */
    public resolveEndpointRoleByIntersection(intersectionT: number): SketchTrimEndpointRole {
        if (intersectionT <= 0.5) {
            return SketchTrimEndpointRole.Start;
        }
        return SketchTrimEndpointRole.End;
    }

    /**
     * 参数排序去重。
     * @param values 参数数组。
     * @returns 排序去重结果。
     */
    private sortAndDedupeParams(values: number[]): number[] {
        const finiteValues = values.filter((value) => Number.isFinite(value));
        finiteValues.sort((left, right) => left - right);

        const deduped: number[] = [];
        finiteValues.forEach((value) => {
            const last = deduped[deduped.length - 1];
            if (last === undefined) {
                deduped.push(value);
                return;
            }
            if (Math.abs(last - value) <= SketchTrimGeometryService.TOPOLOGY_TOLERANCE) {
                return;
            }
            deduped.push(value);
        });
        return deduped;
    }

    /**
     * 参数排序去重（闭合曲线会将 1 收敛到 0）。
     * @param values 参数数组。
     * @param isClosed 是否闭合。
     * @returns 排序去重结果。
     */
    private sortAndDedupeCurveParams(values: number[], isClosed: boolean): number[] {
        const normalizedValues = values.map((value) => {
            if (!isClosed) {
                return value;
            }
            return this.normalizeClosedParameter(value);
        });
        return this.sortAndDedupeParams(normalizedValues);
    }

    //------ 强力剪裁闭合曲线区间解析 开始------
    /**
     * 将闭合曲线参数归一化到 `[0, 1)`。
     *
     * @param value 原始参数。
     * @returns 归一化参数。
     */
    private normalizeClosedParameter(value: number): number {
        if (!Number.isFinite(value)) {
            return 0;
        }

        const normalized = ((value % 1) + 1) % 1;
        if (
            normalized <= SketchTrimGeometryService.TOPOLOGY_TOLERANCE
            || 1 - normalized <= SketchTrimGeometryService.TOPOLOGY_TOLERANCE
        ) {
            return 0;
        }
        return normalized;
    }
    //------ 强力剪裁闭合曲线区间解析 结束------

    /**
     * 从曲线几何中收集采样点。
     * @param curve 目标曲线对象。
     * @param isClosed 是否闭合。
     * @returns 采样点列表。
     */
    private collectSamplePointsFromGeometry(curve: SketchTrimCurveObject, isClosed: boolean): SketchTrimCurveSamplePoint[] {
        const positionAttribute = curve.geometry.getAttribute('position');
        if (!positionAttribute || positionAttribute.count < 2) {
            console.error(`[SketchTrimGeometryService] 曲线采样失败：顶点数量不足，无法参与剪裁：${curve.uuid}`);
            return [];
        }

        const denominator = Math.max(1, positionAttribute.count - 1);
        const samplePoints: SketchTrimCurveSamplePoint[] = [];
        for (let index = 0; index < positionAttribute.count; index += 1) {
            const parameterT = index / denominator;
            samplePoints.push({
                position: new THREE.Vector3(
                    positionAttribute.getX(index),
                    positionAttribute.getY(index),
                    positionAttribute.getZ(index),
                ),
                parameterT: isClosed && parameterT >= 1 - SketchTrimGeometryService.TOPOLOGY_TOLERANCE
                    ? 1
                    : parameterT,
            });
        }

        return samplePoints;
    }

    /**
     * 收集两条曲线的交点参数。
     * @param target 目标曲线。
     * @param candidate 候选曲线。
     * @returns 目标曲线参数列表。
     */
    private collectCurveIntersectionParameters(
        target: SketchTrimCurveData,
        candidate: SketchTrimCurveData,
    ): number[] {
        if (
            target.curveKind === SketchTrimCurveKind.Parabola
            || candidate.curveKind === SketchTrimCurveKind.Parabola
        ) {
            // 只要任意一侧是抛物线，裁剪边界就必须按双方当前显示折线逐段求交，
            // 避免后续新增解析分支时意外改变 Power Trim 已确定的可见几何语义。
            return this.collectCurveIntersectionParametersBySamples(target, candidate);
        }

        const exactLineCircleSplineParameters = this.splineIntersectionService.collectCurveParameters(
            target,
            candidate,
        );
        if (exactLineCircleSplineParameters !== null) {
            return exactLineCircleSplineParameters;
        }

        if (this.isSplineFamilyCurveKind(target.curveKind) || this.isSplineFamilyCurveKind(candidate.curveKind)) {
            // 本次只收口直线/完整圆与样条族组合；其它样条组合继续保持既有兼容语义。
            return this.collectCurveIntersectionParametersBySamples(target, candidate);
        }

        //------ 样条精确裁剪 开始------
        const exactParameters = this.collectExactParametricCurveIntersectionParameters(target, candidate);
        if (exactParameters) {
            return exactParameters;
        }
        //------ 样条精确裁剪 结束------

        //------ Trim 圆与线段精确求交 开始------
        if (target.curveKind === SketchTrimCurveKind.Circle && candidate.curveKind === SketchTrimCurveKind.Line) {
            return this.collectExactCircleLineIntersectionParameters(
                target.curve as ReturnType<typeof this.requireCircleObject>,
                candidate.curve as SketchLineObject,
                true,
            );
        }

        if (target.curveKind === SketchTrimCurveKind.Line && candidate.curveKind === SketchTrimCurveKind.Circle) {
            return this.collectExactCircleLineIntersectionParameters(
                candidate.curve as ReturnType<typeof this.requireCircleObject>,
                target.curve as SketchLineObject,
                false,
            );
        }
        
        if (target.curveKind === SketchTrimCurveKind.Arc && candidate.curveKind === SketchTrimCurveKind.Line) {
            return this.collectExactArcLineIntersectionParameters(
                target.curve as SketchArcObject,
                candidate.curve as SketchLineObject,
                true,
            );
        }
        
        if (target.curveKind === SketchTrimCurveKind.Line && candidate.curveKind === SketchTrimCurveKind.Arc) {
            return this.collectExactArcLineIntersectionParameters(
                candidate.curve as SketchArcObject,
                target.curve as SketchLineObject,
                false,
            );
        }

        //------ Trim 直线与椭圆精确求交 开始------
        if (target.curveKind === SketchTrimCurveKind.Ellipse && candidate.curveKind === SketchTrimCurveKind.Line) {
            return this.collectExactEllipseLineIntersectionParameters(
                target.curve as SketchEllipseObject,
                candidate.curve as SketchLineObject,
                true,
            );
        }

        if (target.curveKind === SketchTrimCurveKind.Line && candidate.curveKind === SketchTrimCurveKind.Ellipse) {
            return this.collectExactEllipseLineIntersectionParameters(
                candidate.curve as SketchEllipseObject,
                target.curve as SketchLineObject,
                false,
            );
        }
        //------ Trim 直线与椭圆精确求交 结束------

        //------ Trim 椭圆与圆精确求交 开始------
        if (target.curveKind === SketchTrimCurveKind.Ellipse && candidate.curveKind === SketchTrimCurveKind.Circle) {
            return this.collectExactEllipseCircleIntersectionParameters(
                target.curve as SketchEllipseObject,
                candidate.curve as SketchCircleObject,
            );
        }

        if (target.curveKind === SketchTrimCurveKind.Circle && candidate.curveKind === SketchTrimCurveKind.Ellipse) {
            return this.collectExactCircleEllipseIntersectionParameters(
                target.curve as SketchCircleObject,
                candidate.curve as SketchEllipseObject,
            );
        }

        if (target.curveKind === SketchTrimCurveKind.Ellipse && candidate.curveKind === SketchTrimCurveKind.Arc) {
            return this.collectExactEllipseArcIntersectionParameters(
                target.curve as SketchEllipseObject,
                candidate.curve as SketchArcObject,
            );
        }

        if (target.curveKind === SketchTrimCurveKind.Arc && candidate.curveKind === SketchTrimCurveKind.Ellipse) {
            return this.collectExactArcEllipseIntersectionParameters(
                target.curve as SketchArcObject,
                candidate.curve as SketchEllipseObject,
            );
        }
        //------ Trim 椭圆与圆精确求交 结束------

        if (target.curveKind === SketchTrimCurveKind.Circle && candidate.curveKind === SketchTrimCurveKind.Circle) {
            return this.collectExactCircleCircleIntersectionParameters(
                target.curve as SketchCircleObject,
                candidate.curve as SketchCircleObject,
            );
        }

        if (target.curveKind === SketchTrimCurveKind.Circle && candidate.curveKind === SketchTrimCurveKind.Arc) {
            return this.collectExactCircleArcIntersectionParameters(
                target.curve as SketchCircleObject,
                candidate.curve as SketchArcObject,
            );
        }

        if (target.curveKind === SketchTrimCurveKind.Arc && candidate.curveKind === SketchTrimCurveKind.Circle) {
            return this.collectExactArcCircleIntersectionParameters(
                target.curve as SketchArcObject,
                candidate.curve as SketchCircleObject,
            );
        }

        if (target.curveKind === SketchTrimCurveKind.Arc && candidate.curveKind === SketchTrimCurveKind.Arc) {
            return this.collectExactArcArcIntersectionParameters(
                target.curve as SketchArcObject,
                candidate.curve as SketchArcObject,
            );
        }

        if (target.curveKind === SketchTrimCurveKind.Ellipse && candidate.curveKind === SketchTrimCurveKind.Ellipse) {
            return this.collectExactEllipseEllipseIntersectionParameters(
                target.curve as SketchEllipseObject,
                candidate.curve as SketchEllipseObject,
            );
        }
        //------ Trim 圆与线段精确求交 结束------

        const params: number[] = [];

        for (let targetIndex = 0; targetIndex < target.samplePoints.length - 1; targetIndex += 1) {
            const targetStart = target.samplePoints[targetIndex];
            const targetEnd = target.samplePoints[targetIndex + 1];

            for (let candidateIndex = 0; candidateIndex < candidate.samplePoints.length - 1; candidateIndex += 1) {
                const candidateStart = candidate.samplePoints[candidateIndex];
                const candidateEnd = candidate.samplePoints[candidateIndex + 1];

                const intersection = this.resolveLineIntersectionParameter(
                    targetStart.position,
                    targetEnd.position,
                    candidateStart.position,
                    candidateEnd.position,
                    target.normal,
                );
                if (!intersection) {
                    continue;
                }

                if (
                    intersection.targetT < -SketchTrimGeometryService.TOPOLOGY_TOLERANCE
                    || intersection.targetT > 1 + SketchTrimGeometryService.TOPOLOGY_TOLERANCE
                    || intersection.sourceT < -SketchTrimGeometryService.TOPOLOGY_TOLERANCE
                    || intersection.sourceT > 1 + SketchTrimGeometryService.TOPOLOGY_TOLERANCE
                ) {
                    continue;
                }

                let parameterT = THREE.MathUtils.lerp(
                    targetStart.parameterT,
                    targetEnd.parameterT,
                    this.clamp01(intersection.targetT),
                );
                if (target.isClosed && parameterT >= 1 - SketchTrimGeometryService.TOPOLOGY_TOLERANCE) {
                    parameterT = 0;
                }
                params.push(parameterT);
            }
        }

        return params;
    }

    /**
     * 使用采样线段收集两条曲线的交点参数。
     * @param target 目标曲线。
     * @param candidate 候选曲线。
     * @returns 目标曲线参数列表。
     */
    private collectCurveIntersectionParametersBySamples(
        target: SketchTrimCurveData,
        candidate: SketchTrimCurveData,
    ): number[] {
        const params: number[] = [];

        for (let targetIndex = 0; targetIndex < target.samplePoints.length - 1; targetIndex += 1) {
            const targetStart = target.samplePoints[targetIndex];
            const targetEnd = target.samplePoints[targetIndex + 1];

            for (let candidateIndex = 0; candidateIndex < candidate.samplePoints.length - 1; candidateIndex += 1) {
                const candidateStart = candidate.samplePoints[candidateIndex];
                const candidateEnd = candidate.samplePoints[candidateIndex + 1];
                const intersection = this.resolveLineIntersectionParameter(
                    targetStart.position,
                    targetEnd.position,
                    candidateStart.position,
                    candidateEnd.position,
                    target.normal,
                );
                if (!intersection) {
                    continue;
                }

                if (
                    intersection.targetT < -SketchTrimGeometryService.TOPOLOGY_TOLERANCE
                    || intersection.targetT > 1 + SketchTrimGeometryService.TOPOLOGY_TOLERANCE
                    || intersection.sourceT < -SketchTrimGeometryService.TOPOLOGY_TOLERANCE
                    || intersection.sourceT > 1 + SketchTrimGeometryService.TOPOLOGY_TOLERANCE
                ) {
                    continue;
                }

                let parameterT = THREE.MathUtils.lerp(
                    targetStart.parameterT,
                    targetEnd.parameterT,
                    this.clamp01(intersection.targetT),
                );
                if (target.isClosed && parameterT >= 1 - SketchTrimGeometryService.TOPOLOGY_TOLERANCE) {
                    parameterT = 0;
                }
                params.push(parameterT);
            }
        }

        return params;
    }

    /**
     * 使用采样线段收集曲线与拖拽线段的交点。
     * @param curveData 目标曲线数据。
     * @param segmentStart 拖拽线段起点。
     * @param segmentEnd 拖拽线段终点。
     * @returns 交点列表。
     */
    private collectCurveSegmentIntersectionsBySamples(
        curveData: SketchTrimCurveData,
        segmentStart: THREE.Vector3,
        segmentEnd: THREE.Vector3,
    ): Array<{targetT: number; sourceT: number; point: THREE.Vector3}> {
        const intersections: Array<{targetT: number; sourceT: number; point: THREE.Vector3}> = [];

        for (let sampleIndex = 0; sampleIndex < curveData.samplePoints.length - 1; sampleIndex += 1) {
            const startSample = curveData.samplePoints[sampleIndex];
            const endSample = curveData.samplePoints[sampleIndex + 1];
            const intersection = this.resolveLineIntersectionParameter(
                startSample.position,
                endSample.position,
                segmentStart,
                segmentEnd,
                curveData.normal,
            );
            if (!intersection) {
                continue;
            }

            if (
                intersection.targetT < -SketchTrimGeometryService.TOPOLOGY_TOLERANCE
                || intersection.targetT > 1 + SketchTrimGeometryService.TOPOLOGY_TOLERANCE
                || intersection.sourceT < -SketchTrimGeometryService.TOPOLOGY_TOLERANCE
                || intersection.sourceT > 1 + SketchTrimGeometryService.TOPOLOGY_TOLERANCE
            ) {
                continue;
            }

            let targetT = THREE.MathUtils.lerp(
                startSample.parameterT,
                endSample.parameterT,
                this.clamp01(intersection.targetT),
            );
            if (curveData.isClosed && targetT >= 1 - SketchTrimGeometryService.TOPOLOGY_TOLERANCE) {
                targetT = 0;
            }

            intersections.push({
                targetT,
                sourceT: this.clamp01(intersection.sourceT),
                point: intersection.point,
            });
        }

        return this.dedupeCurveSegmentIntersections(intersections);
    }

    //------ 强力剪裁拖拽整段精确命中 开始------
    /**
     * 收集直线与拖拽线段的精确交点。
     * @param line 目标直线。
     * @param segmentStart 拖拽线段起点。
     * @param segmentEnd 拖拽线段终点。
     * @returns 交点列表。
     */
    private collectExactLineSegmentIntersections(
        line: SketchLineObject,
        segmentStart: THREE.Vector3,
        segmentEnd: THREE.Vector3,
    ): Array<{targetT: number; sourceT: number; point: THREE.Vector3}> {
        const lineData = this.resolveLineData(line);
        const intersection = this.resolveLineIntersectionParameter(
            lineData.start,
            lineData.end,
            segmentStart,
            segmentEnd,
            lineData.normal,
        );
        if (!intersection) {
            return [];
        }

        if (
            intersection.targetT < -SketchTrimGeometryService.TOPOLOGY_TOLERANCE
            || intersection.targetT > 1 + SketchTrimGeometryService.TOPOLOGY_TOLERANCE
            || intersection.sourceT < -SketchTrimGeometryService.TOPOLOGY_TOLERANCE
            || intersection.sourceT > 1 + SketchTrimGeometryService.TOPOLOGY_TOLERANCE
        ) {
            return [];
        }

        return [{
            targetT: this.clamp01(intersection.targetT),
            sourceT: this.clamp01(intersection.sourceT),
            point: intersection.point,
        }];
    }

    /**
     * 收集圆与拖拽线段的精确交点。
     * @param circle 目标圆。
     * @param segmentStart 拖拽线段起点。
     * @param segmentEnd 拖拽线段终点。
     * @returns 交点列表。
     */
    private collectExactCircleSegmentIntersections(
        circle: SketchCircleObject,
        segmentStart: THREE.Vector3,
        segmentEnd: THREE.Vector3,
    ): Array<{targetT: number; sourceT: number; point: THREE.Vector3}> {
        const referencePlane = circle.referencePlane;
        const intersections = this.circleLineSegmentIntersectionSolver.solve(
            {
                center: referencePlane.worldToLocal(this.resolveCircleCenterWorld(circle)),
                radius: this.resolveCircleRadius(circle),
            },
            {
                start: referencePlane.worldToLocal(segmentStart),
                end: referencePlane.worldToLocal(segmentEnd),
            },
        );

        return intersections.map((intersection) => ({
            targetT: this.normalizeClosedParameter(intersection.circleAngle / (Math.PI * 2)),
            sourceT: intersection.lineParameter,
            // 世界交点直接按原始世界线段参数恢复，避免多一次参考面往返误差。
            point: segmentStart.clone().lerp(segmentEnd, intersection.lineParameter),
        }));
    }

    /**
     * 收集圆弧与拖拽线段的精确交点。
     * @param arc 目标圆弧。
     * @param segmentStart 拖拽线段起点。
     * @param segmentEnd 拖拽线段终点。
     * @returns 交点列表。
     */
    private collectExactArcSegmentIntersections(
        arc: SketchArcObject,
        segmentStart: THREE.Vector3,
        segmentEnd: THREE.Vector3,
    ): Array<{targetT: number; sourceT: number; point: THREE.Vector3}> {
        const referencePlane = arc.referencePlane;
        const intersections = this.arcLineSegmentIntersectionSolver.solveArcLine(
            this.resolveArcIntersectionDefinition(arc, referencePlane),
            {
                start: referencePlane.worldToLocal(segmentStart),
                end: referencePlane.worldToLocal(segmentEnd),
            },
        );

        return this.dedupeCurveSegmentIntersections(intersections.map((intersection) => ({
            targetT: intersection.arcParameter,
            sourceT: intersection.lineParameter,
            // 直接使用拖拽线段参数恢复世界点，避免参考面坐标往返造成端点漂移。
            point: segmentStart.clone().lerp(segmentEnd, intersection.lineParameter),
        })));
    }

    /**
     * 收集椭圆与拖拽线段的精确交点。
     * @param ellipse 目标椭圆。
     * @param segmentStart 拖拽线段起点。
     * @param segmentEnd 拖拽线段终点。
     * @returns 交点列表。
     */
    private collectExactEllipseSegmentIntersections(
        ellipse: SketchEllipseObject,
        segmentStart: THREE.Vector3,
        segmentEnd: THREE.Vector3,
    ): Array<{targetT: number; sourceT: number; point: THREE.Vector3}> {
        const definition = ellipse.getEllipseDefinition();
        const referencePlane = ellipse.referencePlane;
        const intersections = this.ellipseLineSegmentIntersectionSolver.solve(
            {
                center: referencePlane.worldToLocal(definition.centerWorld),
                radiusX: definition.radiusX,
                radiusY: definition.radiusY,
                rotation: definition.rotation,
            },
            {
                start: referencePlane.worldToLocal(segmentStart),
                end: referencePlane.worldToLocal(segmentEnd),
            },
        );

        return this.dedupeCurveSegmentIntersections(intersections.map((intersection) => {
            const point = segmentStart.clone().lerp(segmentEnd, intersection.lineParameter);
            const ellipseParameter = this.projectPointToEllipseParameter(ellipse, point);
            if (ellipseParameter === null) {
                return null;
            }

            return {
                targetT: ellipseParameter,
                sourceT: intersection.lineParameter,
                point,
            };
        }).filter((item): item is {targetT: number; sourceT: number; point: THREE.Vector3} => item !== null));
    }

    /**
     * 对拖拽线段交点做排序与去重。
     * @param intersections 候选交点。
     * @returns 去重后的交点列表。
     */
    private dedupeCurveSegmentIntersections(
        intersections: Array<{targetT: number; sourceT: number; point: THREE.Vector3}>,
    ): Array<{targetT: number; sourceT: number; point: THREE.Vector3}> {
        const sortedIntersections = [...intersections].sort((left, right) => left.sourceT - right.sourceT);
        const dedupedIntersections: Array<{targetT: number; sourceT: number; point: THREE.Vector3}> = [];

        sortedIntersections.forEach((intersection) => {
            if (dedupedIntersections.some((candidate) => candidate.point.distanceToSquared(intersection.point) <= 1e-12)) {
                return;
            }
            dedupedIntersections.push(intersection);
        });

        return dedupedIntersections;
    }
    //------ 强力剪裁拖拽整段精确命中 结束------

    //------ Trim 圆与线段精确求交 开始------
    /**
     * 收集圆与线段的数学精确交点参数。
     *
     * 说明：
     * 1. 这里直接解线段参数二次方程，不再依赖圆几何采样密度；
     * 2. 当 `mapToCircleParameter` 为 true 时，返回圆上的归一化角参数；
     * 3. 当 `mapToCircleParameter` 为 false 时，返回线段上的归一化参数，供“线段目标、圆候选”复用。
     *
     * @param circle 圆对象。
     * @param line 线段对象。
     * @param mapToCircleParameter 是否映射到圆参数。
     * @returns 目标曲线参数列表。
     */
    private collectExactCircleLineIntersectionParameters(
        circle: SketchCircleObject,
        line: SketchLineObject,
        mapToCircleParameter: boolean,
    ): number[] {
        const start = line.startPoint.getPosition();
        const end = line.endPoint.getPosition();
        const intersections = this.collectExactCircleSegmentIntersections(circle, start, end);
        const params = intersections.map((intersection) => (
            mapToCircleParameter ? intersection.targetT : intersection.sourceT
        ));

        return this.sortAndDedupeCurveParams(params, mapToCircleParameter);
    }

    /**
     * 收集圆弧与线段的数学精确交点参数。
     * @param arc 圆弧对象。
     * @param line 线段对象。
     * @param mapToArcParameter 是否映射到圆弧参数。
     * @returns 目标曲线参数列表。
     */
    private collectExactArcLineIntersectionParameters(
        arc: SketchArcObject,
        line: SketchLineObject,
        mapToArcParameter: boolean,
    ): number[] {
        const referencePlane = arc.referencePlane;
        const intersections = this.arcLineSegmentIntersectionSolver.solveArcLine(
            this.resolveArcIntersectionDefinition(arc, referencePlane),
            {
                start: referencePlane.worldToLocal(line.startPoint.getPosition()),
                end: referencePlane.worldToLocal(line.endPoint.getPosition()),
            },
        );
        const params = intersections.map((intersection) => (
            mapToArcParameter ? intersection.arcParameter : intersection.lineParameter
        ));
        return this.sortAndDedupeCurveParams(params, false);
    }

    /**
     * 收集椭圆与线段的数学精确交点参数。
     * @param ellipse 椭圆对象。
     * @param line 线段对象。
     * @param mapToEllipseParameter 是否映射到椭圆参数。
     * @returns 目标曲线参数列表。
     */
    private collectExactEllipseLineIntersectionParameters(
        ellipse: SketchEllipseObject,
        line: SketchLineObject,
        mapToEllipseParameter: boolean,
    ): number[] {
        return this.collectExactEllipseSegmentIntersectionParameters(
            ellipse,
            line.startPoint.getPosition(),
            line.endPoint.getPosition(),
            mapToEllipseParameter,
        );
    }

    /**
     * 收集椭圆与样条显示线段的数学精确交点参数。
     * @param ellipse 目标椭圆。
     * @param splineCurveData 候选样条曲线数据。
     * @returns 目标椭圆参数列表。
     */
    private collectExactEllipseSplineIntersectionParameters(
        ellipse: SketchEllipseObject,
        splineCurveData: SketchTrimCurveData,
    ): number[] {
        const params: number[] = [];

        for (let index = 0; index < splineCurveData.samplePoints.length - 1; index += 1) {
            const startSample = splineCurveData.samplePoints[index];
            const endSample = splineCurveData.samplePoints[index + 1];
            params.push(...this.collectExactEllipseSegmentIntersectionParameters(
                ellipse,
                startSample.position,
                endSample.position,
                true,
            ));
        }

        return this.sortAndDedupeCurveParams(params, this.isFullEllipse(ellipse));
    }

    /**
     * 收集样条显示线段与椭圆的数学精确交点参数。
     * @param splineCurveData 目标样条曲线数据。
     * @param ellipse 候选椭圆。
     * @returns 目标样条参数列表。
     */
    private collectExactSplineEllipseIntersectionParameters(
        splineCurveData: SketchTrimCurveData,
        ellipse: SketchEllipseObject,
    ): number[] {
        const params: number[] = [];

        for (let index = 0; index < splineCurveData.samplePoints.length - 1; index += 1) {
            const startSample = splineCurveData.samplePoints[index];
            const endSample = splineCurveData.samplePoints[index + 1];
            const segmentParams = this.collectExactEllipseSegmentIntersectionParameters(
                ellipse,
                startSample.position,
                endSample.position,
                false,
            );

            segmentParams.forEach((segmentParameter) => {
                let parameterT = THREE.MathUtils.lerp(
                    startSample.parameterT,
                    endSample.parameterT,
                    this.clamp01(segmentParameter),
                );
                if (splineCurveData.isClosed && parameterT >= 1 - SketchTrimGeometryService.TOPOLOGY_TOLERANCE) {
                    parameterT = 0;
                }
                params.push(parameterT);
            });
        }

        return this.sortAndDedupeCurveParams(params, splineCurveData.isClosed);
    }

    /**
     * 收集椭圆与线段的数学精确交点参数。
     * @param ellipse 目标椭圆。
     * @param start 线段起点。
     * @param end 线段终点。
     * @param mapToEllipseParameter 是否映射到椭圆参数。
     * @returns 参数列表。
     */
    private collectExactEllipseSegmentIntersectionParameters(
        ellipse: SketchEllipseObject,
        start: THREE.Vector3,
        end: THREE.Vector3,
        mapToEllipseParameter: boolean,
    ): number[] {
        const definition = ellipse.getEllipseDefinition();
        const referencePlane = ellipse.referencePlane;
        const intersections = this.ellipseLineSegmentIntersectionSolver.solve(
            {
                center: referencePlane.worldToLocal(definition.centerWorld),
                radiusX: definition.radiusX,
                radiusY: definition.radiusY,
                rotation: definition.rotation,
            },
            {
                start: referencePlane.worldToLocal(start),
                end: referencePlane.worldToLocal(end),
            },
        );
        const params: number[] = [];

        intersections.forEach((intersection) => {
            // 数学求解器返回完整支撑椭圆交点，草图层统一过滤部分椭圆的可见参数域。
            const pointWorld = referencePlane.localToWorld(intersection.point);
            const ellipseParameter = this.projectPointToEllipseParameter(ellipse, pointWorld);
            if (ellipseParameter === null) {
                return;
            }

            params.push(mapToEllipseParameter ? ellipseParameter : intersection.lineParameter);
        });

        // 只有返回椭圆参数时才应用完整椭圆的 1→0 接缝归一化；返回直线参数时必须保留终点 1。
        return this.sortAndDedupeCurveParams(params, mapToEllipseParameter && this.isFullEllipse(ellipse));
    }

    /**
     * 收集椭圆与圆的数学精确交点参数。
     * @param ellipse 目标椭圆。
     * @param circle 候选圆。
     * @returns 目标椭圆参数列表。
     */
    private collectExactEllipseCircleIntersectionParameters(
        ellipse: SketchEllipseObject,
        circle: SketchCircleObject,
    ): number[] {
        const intersections = this.resolveExactEllipseCircleIntersectionPoints(ellipse, circle);
        const params = intersections
            .map((point) => this.projectPointToEllipseParameter(ellipse, point))
            .filter((value): value is number => value !== null);
        return this.sortAndDedupeCurveParams(params, this.isFullEllipse(ellipse));
    }

    /**
     * 收集圆与椭圆的数学精确交点参数。
     * @param circle 目标圆。
     * @param ellipse 候选椭圆。
     * @returns 目标圆参数列表。
     */
    private collectExactCircleEllipseIntersectionParameters(
        circle: SketchCircleObject,
        ellipse: SketchEllipseObject,
    ): number[] {
        const intersections = this.resolveExactEllipseCircleIntersectionPoints(ellipse, circle);
        const params = intersections.map((point) => this.projectPointToCircleParameter(circle, point));
        return this.sortAndDedupeCurveParams(params, true);
    }

    /**
     * 收集椭圆与圆弧的数学精确交点参数。
     * @param ellipse 目标椭圆或部分椭圆。
     * @param arc 候选圆弧。
     * @returns 目标椭圆局部参数列表。
     */
    private collectExactEllipseArcIntersectionParameters(
        ellipse: SketchEllipseObject,
        arc: SketchArcObject,
    ): number[] {
        const referencePlane = arc.referencePlane;
        const intersections = this.arcEllipseIntersectionSolver.solveArcEllipse(
            this.resolveArcIntersectionDefinition(arc, referencePlane),
            this.resolveEllipseIntersectionDefinition(ellipse, referencePlane),
        );
        return this.sortAndDedupeCurveParams(
            intersections.map((intersection) => intersection.ellipseParameter),
            this.isFullEllipse(ellipse),
        );
    }

    /**
     * 收集圆弧与椭圆的数学精确交点参数。
     * @param arc 目标圆弧。
     * @param ellipse 候选椭圆或部分椭圆。
     * @returns 目标圆弧局部参数列表。
     */
    private collectExactArcEllipseIntersectionParameters(
        arc: SketchArcObject,
        ellipse: SketchEllipseObject,
    ): number[] {
        const referencePlane = arc.referencePlane;
        const intersections = this.arcEllipseIntersectionSolver.solveArcEllipse(
            this.resolveArcIntersectionDefinition(arc, referencePlane),
            this.resolveEllipseIntersectionDefinition(ellipse, referencePlane),
        );
        return this.sortAndDedupeCurveParams(
            intersections.map((intersection) => intersection.arcParameter),
            false,
        );
    }

    /**
     * 收集两个完整或部分椭圆的数学精确交点参数。
     * @param targetEllipse 目标椭圆。
     * @param candidateEllipse 候选椭圆。
     * @returns 目标椭圆局部参数列表。
     */
    private collectExactEllipseEllipseIntersectionParameters(
        targetEllipse: SketchEllipseObject,
        candidateEllipse: SketchEllipseObject,
    ): number[] {
        const referencePlane = targetEllipse.referencePlane;
        const targetDefinition = targetEllipse.getEllipseDefinition();
        const candidateDefinition = candidateEllipse.getEllipseDefinition();
        const result = this.ellipseEllipseIntersectionSolver.solve(
            {
                center: referencePlane.worldToLocal(targetDefinition.centerWorld),
                radiusX: targetDefinition.radiusX,
                radiusY: targetDefinition.radiusY,
                rotation: targetDefinition.rotation,
            },
            {
                center: referencePlane.worldToLocal(candidateDefinition.centerWorld),
                radiusX: candidateDefinition.radiusX,
                radiusY: candidateDefinition.radiusY,
                rotation: candidateDefinition.rotation,
            },
        );
        if (result.relation === 'coincident') {
            // 无限多个公共点不能形成有限裁剪边界，上层保持重合实体原状。
            return [];
        }

        const parameters: number[] = [];
        result.intersections.forEach((intersection) => {
            const worldPoint = referencePlane.localToWorld(intersection.point);
            // 支撑椭圆求解后必须同时落在两个完整/部分椭圆的当前有限参数域内。
            if (this.projectPointToEllipseParameter(candidateEllipse, worldPoint) === null) {
                return;
            }
            const targetParameter = this.projectPointToEllipseParameter(targetEllipse, worldPoint);
            if (targetParameter !== null) {
                parameters.push(targetParameter);
            }
        });
        return this.sortAndDedupeCurveParams(parameters, this.isFullEllipse(targetEllipse));
    }

    /**
     * 在目标草图参考面的局部二维坐标中计算椭圆与圆的真实交点。
     *
     * 说明：数学求解器只处理二维定义；本适配层负责把草图对象转换为局部定义、
     * 恢复世界坐标，并过滤部分椭圆当前不可见的参数域。
     *
     * @param ellipse 椭圆或部分椭圆。
     * @param circle 完整圆。
     * @returns 当前椭圆参数域内的真实世界交点。
     */
    private resolveExactEllipseCircleIntersectionPoints(
        ellipse: SketchEllipseObject,
        circle: SketchCircleObject,
    ): THREE.Vector3[] {
        const referencePlane = ellipse.referencePlane;
        const ellipseDefinition = ellipse.getEllipseDefinition();
        const intersections = this.ellipseCircleIntersectionSolver.solve(
            {
                center: referencePlane.worldToLocal(ellipseDefinition.centerWorld),
                radiusX: ellipseDefinition.radiusX,
                radiusY: ellipseDefinition.radiusY,
                rotation: ellipseDefinition.rotation,
            },
            {
                center: referencePlane.worldToLocal(this.resolveCircleCenterWorld(circle)),
                radius: this.resolveCircleRadius(circle),
            },
        );

        return intersections
            .map((intersection) => referencePlane.localToWorld(intersection.point))
            .filter((point) => this.projectPointToEllipseParameter(ellipse, point) !== null);
    }

    /**
     * 收集圆与圆的数学精确交点参数。
     * @param targetCircle 目标圆。
     * @param candidateCircle 候选圆。
     * @returns 目标圆参数列表。
     */
    private collectExactCircleCircleIntersectionParameters(
        targetCircle: SketchCircleObject,
        candidateCircle: SketchCircleObject,
    ): number[] {
        const intersections = this.resolveExactCircleCircleIntersectionPoints(targetCircle, candidateCircle);
        const params = intersections.map((point) => this.projectPointToCircleParameter(targetCircle, point));
        return this.sortAndDedupeCurveParams(params, true);
    }

    /**
     * 收集圆与圆弧的数学精确交点参数。
     * @param circle 目标圆。
     * @param arc 候选圆弧。
     * @returns 目标圆参数列表。
     */
    private collectExactCircleArcIntersectionParameters(
        circle: SketchCircleObject,
        arc: SketchArcObject,
    ): number[] {
        const referencePlane = arc.referencePlane;
        const intersections = this.arcCircleIntersectionSolver.solveArcCircle(
            this.resolveArcIntersectionDefinition(arc, referencePlane),
            {
                center: referencePlane.worldToLocal(this.resolveCircleCenterWorld(circle)),
                radius: this.resolveCircleRadius(circle),
            },
        );
        const params = intersections.map((intersection) => this.normalizeClosedParameter(
            intersection.secondCircleAngle / (Math.PI * 2),
        ));
        return this.sortAndDedupeCurveParams(params, true);
    }

    /**
     * 收集圆弧与圆的数学精确交点参数。
     * @param arc 目标圆弧。
     * @param circle 候选圆。
     * @returns 目标圆弧参数列表。
     */
    private collectExactArcCircleIntersectionParameters(
        arc: SketchArcObject,
        circle: SketchCircleObject,
    ): number[] {
        const referencePlane = arc.referencePlane;
        const intersections = this.arcCircleIntersectionSolver.solveArcCircle(
            this.resolveArcIntersectionDefinition(arc, referencePlane),
            {
                center: referencePlane.worldToLocal(this.resolveCircleCenterWorld(circle)),
                radius: this.resolveCircleRadius(circle),
            },
        );
        const params = intersections.map((intersection) => intersection.arcParameter);
        return this.sortAndDedupeCurveParams(params, false);
    }

    /**
     * 收集圆弧与圆弧的数学精确交点参数。
     * @param targetArc 目标圆弧。
     * @param candidateArc 候选圆弧。
     * @returns 目标圆弧参数列表。
     */
    private collectExactArcArcIntersectionParameters(
        targetArc: SketchArcObject,
        candidateArc: SketchArcObject,
    ): number[] {
        const referencePlane = targetArc.referencePlane;
        const intersections = this.arcArcIntersectionSolver.solveArcs(
            this.resolveArcIntersectionDefinition(targetArc, referencePlane),
            this.resolveArcIntersectionDefinition(candidateArc, referencePlane),
        );
        const params = intersections.map((intersection) => intersection.firstArcParameter);
        return this.sortAndDedupeCurveParams(params, false);
    }

    /**
     * 将圆上世界点投影为完整圆归一化参数。
     * @param circle 目标圆。
     * @param point 圆上世界点。
     * @returns 归一化角参数。
     */
    private projectPointToCircleParameter(circle: SketchCircleObject, point: THREE.Vector3): number {
        const centerLocal = circle.referencePlane.worldToLocal(this.resolveCircleCenterWorld(circle));
        const pointLocal = circle.referencePlane.worldToLocal(point);
        const angle = Math.atan2(pointLocal.y - centerLocal.y, pointLocal.x - centerLocal.x);
        const normalizedAngle = angle < 0
            ? angle + Math.PI * 2
            : angle;
        return this.normalizeClosedParameter(normalizedAngle / (Math.PI * 2));
    }

    /**
     * 将圆弧上的世界点投影为归一化弧段参数。
     * @param arc 目标圆弧。
     * @param point 圆弧上世界点。
     * @returns 归一化参数；若点不在弧段有效范围内返回 null。
     */
    private projectPointToArcParameter(arc: SketchArcObject, point: THREE.Vector3): number | null {
        const definition = arc.getArcDefinition();
        const pointLocal = arc.referencePlane.worldToLocal(point);
        const centerLocal = arc.referencePlane.worldToLocal(definition.centerWorld);
        const angle = this.normalizeAngle(Math.atan2(pointLocal.y - centerLocal.y, pointLocal.x - centerLocal.x));
        const sweep = this.computeAngleSweep(definition.startAngle, definition.endAngle, definition.clockwise);
        if (sweep <= SketchTrimGeometryService.ANGLE_TOLERANCE) {
            return null;
        }

        const offset = this.computeAngleOffset(definition.startAngle, angle, definition.clockwise);
        if (
            offset < -SketchTrimGeometryService.ANGLE_TOLERANCE
            || offset > sweep + SketchTrimGeometryService.ANGLE_TOLERANCE
        ) {
            return null;
        }

        return this.clamp01(offset / sweep);
    }

    /**
     * 将椭圆上的世界点投影为归一化椭圆参数。
     * @param ellipse 目标椭圆。
     * @param point 椭圆上世界点。
     * @returns 归一化参数；若点不在部分椭圆有效范围内返回 null。
     */
    private projectPointToEllipseParameter(ellipse: SketchEllipseObject, point: THREE.Vector3): number | null {
        const definition = ellipse.getEllipseDefinition();
        return this.ellipseGeometryService.projectWorldPointToParameter(
            ellipse.referencePlane,
            definition,
            point,
            SketchTrimGeometryService.ANGLE_TOLERANCE,
        );
    }

    /**
     * 解析两个圆的数学精确交点。
     * @param first 第一个圆。
     * @param second 第二个圆。
     * @returns 交点列表。
     */
    private resolveExactCircleCircleIntersectionPoints(
        first: SketchCircleObject,
        second: SketchCircleObject,
    ): THREE.Vector3[] {
        const referencePlane = first.referencePlane;
        const firstCenterLocal = referencePlane.worldToLocal(this.resolveCircleCenterWorld(first));
        const secondCenterLocal = referencePlane.worldToLocal(this.resolveCircleCenterWorld(second));
        const intersections = this.circleCircleIntersectionSolver.solve(
            {
                center: firstCenterLocal,
                radius: this.resolveCircleRadius(first),
            },
            {
                center: secondCenterLocal,
                radius: this.resolveCircleRadius(second),
            },
        );

        return intersections.map((intersection) => referencePlane.localToWorld(intersection.point));
    }

    /**
     * 将草图圆弧适配为数学求解器定义。
     *
     * @param arc 草图圆弧对象。
     * @param referencePlane 统一求交参考面。
     * @returns 不依赖草图对象的二维圆弧定义。
     */
    private resolveArcIntersectionDefinition(
        arc: SketchArcObject,
        referencePlane: ReferencePlane,
    ): CircularArcIntersectionDefinition2D {
        const definition = arc.getArcDefinition();
        return {
            center: referencePlane.worldToLocal(definition.centerWorld),
            radius: definition.radius,
            startAngle: definition.startAngle,
            endAngle: definition.endAngle,
            clockwise: definition.clockwise,
            isFull: false,
        };
    }

    /**
     * 将草图椭圆适配为数学求解器定义。
     *
     * @param ellipse 草图椭圆对象。
     * @param referencePlane 统一求交参考面。
     * @returns 不依赖草图对象的二维完整或部分椭圆定义。
     */
    private resolveEllipseIntersectionDefinition(
        ellipse: SketchEllipseObject,
        referencePlane: ReferencePlane,
    ): EllipticalArcIntersectionDefinition2D {
        const definition = ellipse.getEllipseDefinition();
        return {
            center: referencePlane.worldToLocal(definition.centerWorld),
            radiusX: definition.radiusX,
            radiusY: definition.radiusY,
            rotation: definition.rotation,
            startAngle: definition.startAngle,
            endAngle: definition.endAngle,
            clockwise: definition.clockwise,
            isFull: this.isFullEllipse(ellipse),
        };
    }

    /**
     * 约束圆对象类型，避免在精确求交流程中误传其它闭合曲线。
     * @param circle 候选圆对象。
     * @returns 圆对象。
     */
    private requireCircleObject(circle: SketchTrimCurveObject): SketchCircleObject {
        if (!isSketchCircleObject(circle)) {
            console.error(`[SketchTrimGeometryService] 当前曲线并非草图圆，无法执行圆精确求交：${circle.uuid}`);
        }
        return circle as SketchCircleObject;
    }

    /**
     * 解析圆心世界坐标。
     * @param circle 目标圆。
     * @returns 圆心世界坐标。
     */
    private resolveCircleCenterWorld(circle: SketchCircleObject): THREE.Vector3 {
        if ('getCenterWorld' in circle && typeof circle.getCenterWorld === 'function') {
            return circle.getCenterWorld();
        }

        if ('getCenterControlPoint' in circle && typeof circle.getCenterControlPoint === 'function') {
            return circle.getCenterControlPoint().getPosition();
        }

        console.error(`[SketchTrimGeometryService] 草图圆缺少圆心读取能力，无法执行圆精确求交：${circle.uuid}`);
        return new THREE.Vector3();
    }

    /**
     * 解析圆半径。
     * @param circle 目标圆。
     * @returns 圆半径。
     */
    private resolveCircleRadius(circle: SketchCircleObject): number {
        if ('getRadius' in circle && typeof circle.getRadius === 'function') {
            return circle.getRadius();
        }

        if ('radius' in circle && typeof circle.radius === 'number') {
            return circle.radius;
        }

        console.error(`[SketchTrimGeometryService] 草图圆缺少半径读取能力，无法执行圆精确求交：${circle.uuid}`);
        return 0;
    }

    /**
     * 将角度归一化到 `[0, 2π)`。
     * @param angle 原始角度。
     * @returns 归一化角度。
     */
    private normalizeAngle(angle: number): number {
        return ((angle % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
    }

    /**
     * 计算曲线总扫角。
     * @param startAngle 起始角。
     * @param endAngle 终止角。
     * @param clockwise 是否顺时针。
     * @returns 扫角。
     */
    private computeAngleSweep(startAngle: number, endAngle: number, clockwise: boolean): number {
        const normalizedStart = this.normalizeAngle(startAngle);
        const normalizedEnd = this.normalizeAngle(endAngle);
        return clockwise
            ? ((normalizedStart - normalizedEnd) + Math.PI * 2) % (Math.PI * 2)
            : ((normalizedEnd - normalizedStart) + Math.PI * 2) % (Math.PI * 2);
    }

    /**
     * 计算角度相对起始角的有向偏移。
     * @param startAngle 起始角。
     * @param angle 当前角。
     * @param clockwise 是否顺时针。
     * @returns 有向偏移。
     */
    private computeAngleOffset(startAngle: number, angle: number, clockwise: boolean): number {
        const normalizedStart = this.normalizeAngle(startAngle);
        const normalizedAngle = this.normalizeAngle(angle);
        return clockwise
            ? ((normalizedStart - normalizedAngle) + Math.PI * 2) % (Math.PI * 2)
            : ((normalizedAngle - normalizedStart) + Math.PI * 2) % (Math.PI * 2);
    }
    //------ Trim 圆与线段精确求交 结束------

    /**
     * 判断椭圆是否为完整闭合椭圆。
     * @param ellipse 目标椭圆。
     * @returns 若为完整椭圆返回 true。
     */
    private isFullEllipse(ellipse: SketchEllipseObject): boolean {
        const definition = ellipse.getEllipseDefinition();
        const angleSpan = Math.abs(definition.endAngle - definition.startAngle);
        const normalizedSpan = angleSpan % (Math.PI * 2);
        return normalizedSpan < SketchTrimGeometryService.ANGLE_TOLERANCE
            || Math.abs(normalizedSpan - Math.PI * 2) < SketchTrimGeometryService.ANGLE_TOLERANCE;
    }

    /**
     * 归一化区间。
     * @param startT 区间起点。
     * @param endT 区间终点。
     * @returns 归一化区间；长度不足返回 null。
     */
    private normalizeInterval(startT: number, endT: number): SketchTrimInterval | null {
        const normalizedStart = Math.min(startT, endT);
        const normalizedEnd = Math.max(startT, endT);
        if (normalizedEnd - normalizedStart <= SketchTrimGeometryService.TOPOLOGY_TOLERANCE) {
            return null;
        }
        return {
            startT: normalizedStart,
            endT: normalizedEnd,
        };
    }

    /**
     * 解析两个方向向量之间的无符号夹角。
     * @param left 左侧方向。
     * @param right 右侧方向。
     * @returns 夹角弧度，范围 [0, π/2]。
     */
    private resolveUnsignedAngleBetweenDirections(left: THREE.Vector3, right: THREE.Vector3): number {
        const cosine = Math.abs(left.dot(right));
        const clampedCosine = Math.min(1, Math.max(-1, cosine));
        return Math.acos(clampedCosine);
    }
}
